<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import {
  BaseMessage,
  MessageType,
  createMessage,
  isValidMessage,
  PtyOpenPayload,
  PtyOpenedPayload,
  PtyOutputPayload,
  PtyClosedPayload,
  NotifyEventPayload,
  ProjectInfo,
  ProjectListResultPayload,
  ProjectFocusResultPayload,
  FolderEntry,
  FolderListResPayload,
  OpenProjectResultPayload,
  ProjectCloseResultPayload,
  TaskCardPayload,
  ChatEventPayload,
  AttentionRingPayload,
  AttentionItemPayload,
  ActivityTickPayload,
  AppDebugCmdPayload,
  AppDebugResultPayload,
  ProjectLive,
  StatusPayload,
  BridgeSessionInfo,
  BridgeEventPayload,
  BridgeSendResultPayload,
  matchVoiceConfirm,
  voiceBroadcast,
} from '@kimi-remote/shared';
import { renderMarkdown } from './markdown';

const RELAY_URL = import.meta.env.VITE_RELAY_URL || 'ws://localhost:8080';
const TOKEN_STORAGE_KEY = 'kimi-remote-token';
/** SaaS 控制台地址（手机号登录换 relay 票据） */
const CONSOLE_BASE = 'https://console.ymgymg.xin';

// APP 壳内：底部空间由原生 margin 精确管理（导航栏/键盘），web 侧不再自抬，防双抬
if (navigator.userAgent.includes('YuzaiApp')) {
  document.documentElement.style.setProperty('--nav-lift', '0px');
}
const DEVICE_ID = (() => {
  // 稳定设备号（远程调试的寻址身份；同装重连自然踢旧，防双连接）
  let id = localStorage.getItem('yuzai-client-id');
  if (!id) {
    id = `client-${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem('yuzai-client-id', id);
  }
  return id;
})();

// 深度链接登录（与官方 kimi web 同款 #token= 用法）：APP 深链/浏览器把令牌带来，落地即清出地址栏
const hashToken = (() => {
  const m = location.hash.match(/[#&]token=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : '';
})();
if (hashToken) {
  localStorage.setItem(TOKEN_STORAGE_KEY, hashToken);
  history.replaceState(null, '', location.pathname + location.search);
}

const token = ref<string>(
  import.meta.env.VITE_AUTH_TOKEN || localStorage.getItem(TOKEN_STORAGE_KEY) || ''
);
const needsLogin = ref(!token.value);
const loginInput = ref('');
const loginError = ref('');

const connected = ref(false);
const authenticated = ref(false);
const agentOnline = ref(false);
const terminalRef = ref<HTMLElement>();
const statusText = ref('未连接');

// ---- 持久 PTY 终端 ----
const ptyActive = ref(false);
const ctrlArmed = ref(false);
/** 默认进 tmux main 会话（断线重连后 attach 即回） */
const PTY_CMD = 'tmux new -A -s main';
let ptyId = '';
let ptyOpening = false;
let ptyOpenTimer: number | null = null;
let ptyRetryTimer: number | null = null;
let ptyRetryCount = 0;
let onWinResize: (() => void) | null = null;
const PTY_MAX_RETRY = 5;
const PTY_OPEN_TIMEOUT = 6000;

// 页面被刷新/关闭时尽力通知 agent 释放 PTY（onUnmounted 覆盖不到刷新场景）
function onPageHide() {
  if (ptyId && ws && ws.readyState === WebSocket.OPEN) {
    send(createMessage(MessageType.PTY_CLOSE, { ptyId }));
  }
}

// ---- 状态桥（桌面会话）----
const view = ref<'projects' | 'terminal' | 'bridge' | 'plugins'>('projects');
/** 界面分级（六·十五）：SaaS 用户（yzt_ 手机号登录）只见项目页；admin（自用 token）全开 */
const isSaaS = computed(() => token.value.startsWith('yzt_'));
const bridgeMode = ref<'list' | 'chat'>('list');
const bridgeSessions = ref<BridgeSessionInfo[]>([]);
const activeSession = ref<BridgeSessionInfo | null>(null);
const bridgeEvents = ref<BridgeEventPayload[]>([]);
const bridgeInput = ref('');
const bridgeSendError = ref('');
const chatRef = ref<HTMLElement>();
// 智能钉底：用户在底部才跟随滚动；上翻阅读时新消息不拽走视图，只攒「新消息」浮钮
const bridgePinned = ref(true);
const bridgeNewCount = ref(0);

const groupedSessions = computed(() => {
  const map = new Map<string, BridgeSessionInfo[]>();
  for (const s of bridgeSessions.value) {
    const project = s.project || (s.cwd ? s.cwd.split('/').filter(Boolean).pop() || s.cwd : '(未分组)');
    if (!map.has(project)) map.set(project, []);
    map.get(project)!.push(s);
  }
  // 每组只摆最近 20 条，更早的折叠成一行计数（几千条全铺开没法看）
  return [...map.entries()].map(([project, sessions]) => ({
    project,
    sessions: sessions.slice(0, 20),
    more: Math.max(0, sessions.length - 20),
  }));
});

// ---- 插件中心（五期·十一，admin 视图） ----
interface PluginItem {
  name: string;
  title: string;
  description: string;
  risk: 'L0' | 'L1' | 'L2' | 'L3';
  enabled: boolean;
  hasSelftest: boolean;
  executor: 'http' | 'mcp';
}
const pluginsList = ref<PluginItem[]>([]);
const pluginsLoading = ref(false);
const pluginTestResult = ref<Record<string, { ok: boolean; detail: string }>>({});

function openPlugins() {
  view.value = 'plugins';
  refreshPlugins();
}

function refreshPlugins() {
  pluginsLoading.value = true;
  send(createMessage(MessageType.PLUGINS_LIST, {}));
}

function runPluginSelftest(name: string) {
  pluginTestResult.value = { ...pluginTestResult.value, [name]: { ok: true, detail: '自检中…' } };
  send(createMessage(MessageType.PLUGINS_SELFTEST, { name }));
}

function togglePlugin(p: PluginItem) {
  send(createMessage(MessageType.PLUGINS_SET_ENABLED, { name: p.name, enabled: !p.enabled }));
  p.enabled = !p.enabled;
}

const projects = ref<ProjectInfo[]>([]);
const projectsLoading = ref(false);
const projectsError = ref('');
let projectsTimer: number | null = null;
let pendingFocusPaneId = '';

// ---- 待查看闹铃 & 活性心跳 ----
const attentionItems = ref<AttentionItemPayload[]>([]);
// 待查看横幅：默认折成一行（两区制——项目一排灯+对话一片，点标题才展开）
const attnCollapsed = ref(true);
const liveMap = ref<Record<string, ProjectLive>>({});
const monitorDead = ref(false);
let lastTickAt = 0;
let monitorCheckTimer: number | null = null;

// ---- APP 内建远程调试（五期·十八）：同租户定向；只答 '*' 或点我名的指令 ----
interface YzDebugNative {
  screenshotBase64(): string;
  reload(clearCache: boolean): void;
  tap(x: number, y: number): void;
}
function nativeDebug(): YzDebugNative | null {
  const d = (window as unknown as { YuzaiDebug?: YzDebugNative }).YuzaiDebug;
  return d || null;
}

function replyDebug(reqId: string, ok: boolean, text?: string, image?: string) {
  send(createMessage<AppDebugResultPayload>(MessageType.APP_DEBUG_RESULT, { reqId, deviceId: DEVICE_ID, ok, text, image }));
}

async function handleDebugCmd(p: AppDebugCmdPayload) {
  if (!p || (p.targetDeviceId !== '*' && p.targetDeviceId !== DEVICE_ID)) return;
  const nd = nativeDebug();
  try {
    switch (p.action) {
      case 'state': {
        replyDebug(p.reqId, true, JSON.stringify({
          view: view.value,
          loginMode: token.value.startsWith('yzt_') ? 'phone' : 'token',
          projects: projects.value.length,
          voiceState: voiceState.value,
          ua: navigator.userAgent.slice(0, 120),
          url: location.href,
        }));
        break;
      }
      case 'screenshot': {
        if (!nd) {
          replyDebug(p.reqId, false, '截屏只在 APP 里可用（浏览器没有这个能力）');
          break;
        }
        const img = nd.screenshotBase64();
        replyDebug(p.reqId, true, undefined, img);
        break;
      }
      case 'reload': {
        replyDebug(p.reqId, true, '重载中（清缓存）');
        if (nd) nd.reload(true);
        else location.reload();
        break;
      }
      case 'set_token': {
        if (!p.text) {
          replyDebug(p.reqId, false, '没带令牌');
          break;
        }
        localStorage.setItem(TOKEN_STORAGE_KEY, p.text);
        replyDebug(p.reqId, true, '令牌已写入，重载生效');
        location.reload();
        break;
      }
      case 'tap': {
        if (!nd) {
          replyDebug(p.reqId, false, '点按只在 APP 里可用');
          break;
        }
        nd.tap(Math.round(p.x || 0), Math.round(p.y || 0));
        replyDebug(p.reqId, true);
        break;
      }
      default:
        replyDebug(p.reqId, false, `不认识的指令: ${String(p.action)}`);
    }
  } catch (err) {
    replyDebug(p.reqId, false, String((err as Error)?.message || err).slice(0, 120));
  }
}

function handleAttentionRing(p: AttentionRingPayload) {
  attentionItems.value = p.items;
  playBell();
}

function ackItem(item: AttentionItemPayload) {
  send(createMessage(MessageType.ATTENTION_ACK, { paneId: item.paneId, key: item.key }));
  attentionItems.value = attentionItems.value.filter((i) => i.key !== item.key);
  if (item.paneId) {
    const proj = projects.value.find((p) => p.paneId === item.paneId);
    if (proj) focusProject(proj);
  }
}

function ackAll() {
  send(createMessage(MessageType.ATTENTION_ACK, { all: true }));
  attentionItems.value = [];
}

/** 这个项目有没有待查看（卡片上的红点） */
function attnOf(p: ProjectInfo): boolean {
  return attentionItems.value.some((i) => i.paneId === p.paneId || i.project === p.project);
}

function handleActivityTick(p: ActivityTickPayload) {
  lastTickAt = Date.now();
  monitorDead.value = false;
  const map: Record<string, ProjectLive> = {};
  for (const it of p.items) map[it.paneId] = it.live;
  liveMap.value = map;
}

function liveOf(p: ProjectInfo): ProjectLive {
  return liveMap.value[p.paneId] || p.live || (p.state === 'idle' ? 'none' : 'quiet');
}

function liveLabel(l: ProjectLive): string {
  if (l === 'active') return '活跃';
  if (l === 'quiet') return '静默';
  if (l === 'stalled') return '疑似卡住';
  return '';
}

function startMonitorCheck() {
  if (monitorCheckTimer) clearInterval(monitorCheckTimer);
  monitorCheckTimer = window.setInterval(() => {
    if (!lastTickAt) return;
    if (Date.now() - lastTickAt > 50000) monitorDead.value = true;
  }, 5000);
}

function stopMonitorCheck() {
  if (monitorCheckTimer) {
    clearInterval(monitorCheckTimer);
    monitorCheckTimer = null;
  }
}

// ---- 对话式总管 ----
interface ChatMsg {
  kind: 'user' | 'text' | 'tool' | 'confirm_req' | 'confirm_done' | 'error' | 'card';
  text: string;
  confirmId?: string;
  ts: number;
  answered?: boolean;
  /** 图片消息（dataURL，仅用户气泡） */
  image?: string;
  /** 汇报卡片字段（kind=card：项目名/详情/建议，进对话流） */
  project?: string;
  detail?: string;
  suggestion?: string;
  cardKind?: string;
}
const chatMessages = ref<ChatMsg[]>([]);
const chatInput = ref('');
const chatBusy = ref(false);
const chatPanelRef = ref<HTMLElement>();
const chatPinned = ref(true);
const chatNewCount = ref(0);
const chatInputEl = ref<HTMLTextAreaElement>();
const chatMenuOpen = ref(false);

/** 输入框自动撑高：短文字跟随（最多约 2 行，超出内部滚动，不占屏）；长文字点 ⤢ 进大编辑模式 */
const inputExpanded = ref(false);
function autoGrowChatInput() {
  const el = chatInputEl.value;
  if (!el) return;
  const cap = inputExpanded.value ? Math.round(window.innerHeight * 0.45) : 64;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, cap) + 'px';
  el.style.overflowY = el.scrollHeight > cap ? 'auto' : 'hidden';
}
/** 用户当前聚焦的项目窗格（"这个项目/它"的指代，随 CHAT_SEND 发给总管） */
const focusedPaneId = ref('');

// ---- 点选开/关项目（五期·十九）：点选是主通道（真实列表零误识别），说名字保留为辅助 ----
/** 点选抽屉：null=关着；loading=清单在路上 */
const folderPicker = ref<{ loading: boolean; folders: FolderEntry[]; failedRoots: string[] } | null>(null);
/** 点中后的本地二次确认 */
const folderConfirm = ref<FolderEntry | null>(null);
/** 开立中（等 OPEN_PROJECT_RESULT 回执） */
const folderOpening = ref('');
/** 关项目确认（卡片 ✕ 点开后先弹这个） */
const closeConfirm = ref<ProjectInfo | null>(null);

/** 抽屉排序：用户目录（家目录，项目大多在这）排最前，再桌面/文稿/下载，其余自定义根殿后；组内按名字排 */
const folderPickerSorted = computed(() => {
  if (!folderPicker.value) return [];
  const ORDER: Record<string, number> = { 用户目录: 0, 桌面: 1, 文稿: 2, 下载: 3 };
  return [...folderPicker.value.folders].sort(
    (a, b) => (ORDER[a.where] ?? 9) - (ORDER[b.where] ?? 9) || a.label.localeCompare(b.label, 'zh-Hans-CN')
  );
});

function openFolderPicker() {
  folderPicker.value = { loading: true, folders: [], failedRoots: [] };
  folderConfirm.value = null;
  if (!send(createMessage(MessageType.FOLDER_LIST_REQ, {}))) {
    folderPicker.value = null;
    chatMessages.value.push({ kind: 'error', text: '没连上服务器，文件夹清单没拉成。', ts: Date.now() });
  }
}

/** 路径显示成人话（~/Desktop/XX 风格；Windows 盘符同理缩写） */
function fmtFolderPath(p: string): string {
  return p.replace(/^\/Users\/[^/]+/, '~').replace(/^[A-Za-z]:[\\/]+Users[\\/]+[^\\/]+/i, '~');
}

/** 本地二次确认后正式开立（agent 侧还会再验一次根目录白名单） */
function confirmOpenFolder() {
  const f = folderConfirm.value;
  if (!f) return;
  folderConfirm.value = null;
  folderOpening.value = f.path;
  send(createMessage(MessageType.OPEN_PROJECT, { path: f.path, label: f.label }));
}

/** 卡片 ✕：先弹确认（防误触），确认了才发 PROJECT_CLOSE */
function askCloseProject(p: ProjectInfo) {
  closeConfirm.value = p;
}

function doCloseProject() {
  const p = closeConfirm.value;
  if (!p) return;
  closeConfirm.value = null;
  send(createMessage(MessageType.PROJECT_CLOSE, { paneId: p.paneId }));
}
/** 待发送的图片（base64 无前缀 + dataURL 预览） */
const pendingImage = ref('');
const pendingImagePreview = ref('');
const imageViewer = ref('');
const imageFileInput = ref<HTMLInputElement>();

function pickImage() {
  imageFileInput.value?.click();
}

function onImagePicked(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = ''; // 允许重选同一张
  if (!file) return;
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
    chatMessages.value.push({ kind: 'error', text: '只支持 JPG/PNG/WebP 图片。', ts: Date.now() });
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      // 缩到最长边 1024（够看清字，省流量省票据额度）
      const scale = Math.min(1, 1024 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      if (dataUrl.length > 2.6 * 1024 * 1024) {
        chatMessages.value.push({ kind: 'error', text: '图片太大了，裁剪一下或换张小点的。', ts: Date.now() });
        return;
      }
      pendingImagePreview.value = dataUrl;
      pendingImage.value = dataUrl.split(',')[1] || '';
    };
    img.src = String(reader.result);
  };
  reader.readAsDataURL(file);
}

function clearPendingImage() {
  pendingImage.value = '';
  pendingImagePreview.value = '';
}

function sendChat() {
  const text = chatInput.value.trim();
  const img = pendingImage.value;
  if ((!text && !img) || chatBusy.value) return;
  chatMessages.value.push({ kind: 'user', text, image: pendingImagePreview.value || undefined, ts: Date.now() });
  chatInput.value = '';
  clearPendingImage();
  chatMenuOpen.value = false;
  inputExpanded.value = false; // 发完收回小条
  nextTick(() => {
    if (chatInputEl.value) chatInputEl.value.style.height = 'auto';
  });
  chatBusy.value = true;
  if (!send(createMessage(MessageType.CHAT_SEND, { text, focusPaneId: focusedPaneId.value || undefined, image: img || undefined }))) {
    chatMessages.value.push({ kind: 'error', text: '没连上服务器，这句话没发出去。', ts: Date.now() });
    chatBusy.value = false;
    return;
  }
  // 超时兜底：断线/agent 无响应时不得永久禁用输入框
  if (chatBusyTimer) clearTimeout(chatBusyTimer);
  chatBusyTimer = window.setTimeout(() => {
    if (chatBusy.value) {
      chatBusy.value = false;
      chatMessages.value.push({ kind: 'error', text: '总管这会儿没回应（断线或太忙），请再说一遍。', ts: Date.now() });
    }
  }, 90000);
  scrollChatPanel(true);
}

let chatBusyTimer: number | null = null;

function answerConfirm(confirmId: string, approve: boolean) {
  const msg = chatMessages.value.find((m) => m.confirmId === confirmId);
  if (msg) msg.answered = true;
  send(createMessage(MessageType.CHAT_CONFIRM, { confirmId, approve }));
}

/** 「💡下一步」快捷钮：让总管按当前（聚焦）项目状态给建议 */
function askNextSteps() {
  if (chatBusy.value) return;
  chatInput.value = '下一步建议';
  sendChat();
}

function handleChatEvent(p: ChatEventPayload) {
  chatBusy.value = false;
  if (chatBusyTimer) {
    clearTimeout(chatBusyTimer);
    chatBusyTimer = null;
  }
  // focus 工具：客户端执行真实切换（拿 paneId 走 PROJECT_FOCUS）
  if (p.kind === 'tool' && p.text.startsWith('__FOCUS__')) {
    const paneId = p.text.slice('__FOCUS__'.length);
    focusedPaneId.value = paneId;
    chatMessages.value.push({ kind: 'tool', text: `带你去看「${p.project || '项目'}」…`, ts: p.ts });
    if (ptyActive.value) {
      send(createMessage(MessageType.PROJECT_FOCUS, { paneId, ptyId }));
    } else {
      pendingFocusPaneId = paneId;
      openPty(true);
    }
    scrollChatPanel();
    return;
  }
  chatMessages.value.push({ kind: p.kind, text: p.text, confirmId: p.confirmId, ts: p.ts });
  if (p.kind === 'text') {
    if (voiceMode.value) {
      voiceState.value = 'speaking';
      speak(voiceBroadcast(p.text), voiceLoopContinue);
    } else {
      speakAuto(p.text);
    }
  } else if (p.kind === 'confirm_req' && voiceMode.value) {
    pendingVoiceConfirmId = p.confirmId || null;
    voiceState.value = 'speaking';
    speak(`${voiceBroadcast(p.text)}。说「确认」执行，说「取消」作废`, voiceLoopContinue);
  }
  if (chatMessages.value.length > 200) chatMessages.value.splice(0, 50);
  scrollChatPanel();
}

function scrollChatPanel(force = false) {
  nextTick(() => {
    const el = chatPanelRef.value;
    if (!el) return;
    if (force || chatPinned.value) {
      el.scrollTop = el.scrollHeight;
      chatPinned.value = true;
      chatNewCount.value = 0;
    } else {
      chatNewCount.value++;
    }
  });
}

function onChatScroll() {
  const el = chatPanelRef.value;
  if (!el) return;
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  chatPinned.value = atBottom;
  if (atBottom) chatNewCount.value = 0;
}

// ---- 视图导航（硬件返回键支持：History API + APP canGoBack） ----
let navRestoring = false;

function pushNav() {
  if (navRestoring) return;
  const state = { view: view.value };
  const cur = history.state;
  if (cur && cur.view === state.view) return; // 不堆重复态
  history.pushState(state, '');
}

function applyNavState(state: { view?: string } | null) {
  // popstate 恢复：只回顶层视图，bridge 一律回到列表（chat 现场不可简单复原，安全取舍）
  navRestoring = true;
  try {
    const v = state?.view;
    if (v === 'terminal') openTerminalView();
    else if (v === 'bridge') openBridge();
    else openProjects();
  } finally {
    navRestoring = false;
  }
}

/** WebView(overview 缩放模式)可能把页面向下滚动露出内容但藏住 header，视图切换/进页面时拉回顶 */
function scrollTop() {
  nextTick(() => window.scrollTo(0, 0));
}

window.addEventListener('popstate', (e: PopStateEvent) => {
  applyNavState(e.state as { view?: string } | null);
});

function openProjects() {
  view.value = 'projects';
  refreshProjects();
  startProjectsTimer();
  pushNav();
  scrollTop();
}

function refreshProjects() {
  if (!authenticated.value) return;
  projectsLoading.value = true;
  send(createMessage(MessageType.PROJECT_LIST, {}));
}

function startProjectsTimer() {
  stopProjectsTimer();
  projectsTimer = window.setInterval(() => {
    if (view.value === 'projects') refreshProjects();
  }, 15000);
}

function stopProjectsTimer() {
  if (projectsTimer) {
    clearInterval(projectsTimer);
    projectsTimer = null;
  }
}

function focusProject(p: ProjectInfo) {
  projectsError.value = '';
  focusedPaneId.value = p.paneId;
  if (!ptyActive.value) {
    // 终端尚未连接：先打开 PTY，成功后在 handlePtyOpened 里补发聚焦
    pendingFocusPaneId = p.paneId;
    openPty(true);
    return;
  }
  send(createMessage(MessageType.PROJECT_FOCUS, { paneId: p.paneId, ptyId }));
}

function stateLabel(p: ProjectInfo): string {
  if (p.state === 'ai') return '🤖 AI 工作中';
  if (p.state === 'running') return `⏳ ${p.runningCmd || '执行中'}`;
  return '💤 空闲';
}

function fmtDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}分钟`;
  return `${Math.floor(sec / 3600)}小时${Math.floor((sec % 3600) / 60)}分`;
}

function openBridge() {
  stopProjectsTimer();
  view.value = 'bridge';
  pushNav();
  if (bridgeMode.value === 'list') {
    send(createMessage(MessageType.BRIDGE_LIST_SESSIONS, {}));
  }
}

function openTerminalView() {
  stopProjectsTimer();
  view.value = 'terminal';
  pushNav();
  scrollTop();
  nextTick(() => {
    fitAddon?.fit();
    if (!ptyActive.value) openPty(true);
    term?.focus();
  });
}

function refreshSessions() {
  send(createMessage(MessageType.BRIDGE_LIST_SESSIONS, {}));
}

function openSession(s: BridgeSessionInfo) {
  activeSession.value = s;
  bridgeEvents.value = [];
  bridgeSendError.value = '';
  bridgePinned.value = true;
  bridgeNewCount.value = 0;
  bridgeMode.value = 'chat';
  send(createMessage(MessageType.BRIDGE_WATCH, { sessionId: s.sessionId }));
}

function backToList() {
  if (activeSession.value) {
    send(createMessage(MessageType.BRIDGE_UNWATCH, { sessionId: activeSession.value.sessionId }));
  }
  activeSession.value = null;
  bridgeMode.value = 'list';
  refreshSessions();
}

function sendBridgeMessage() {
  const text = bridgeInput.value.trim();
  if (!text || !activeSession.value) return;
  bridgeSendError.value = '';
  if (!send(createMessage(MessageType.BRIDGE_SEND, { sessionId: activeSession.value.sessionId, text }))) {
    bridgeSendError.value = '未连接，消息未发送';
    return; // 保留输入不清空
  }
  bridgeInput.value = '';
}

function roleLabel(ev: BridgeEventPayload): string {
  if (ev.kind === 'user' || ev.role === 'user') return '你';
  if (ev.role === 'think') return '思考';
  if (ev.role === 'assistant') return 'Kimi';
  return ev.role || '';
}

function formatTime(ts: number): string {
  if (!ts) return '';
  const diff = Date.now() - ts;
  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
  return new Date(ts).toLocaleDateString();
}

function scrollChatToBottom(force = false) {
  nextTick(() => {
    const el = chatRef.value;
    if (!el) return;
    if (force || bridgePinned.value) {
      el.scrollTop = el.scrollHeight;
      bridgePinned.value = true;
      bridgeNewCount.value = 0;
    } else {
      bridgeNewCount.value++;
    }
  });
}

function onBridgeScroll() {
  const el = chatRef.value;
  if (!el) return;
  const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  bridgePinned.value = atBottom;
  if (atBottom) bridgeNewCount.value = 0;
}

let ws: WebSocket | null = null;
let term: Terminal | null = null;
let fitAddon: FitAddon | null = null;
let reconnectTimer: number | null = null;
let heartbeatTimer: number | null = null;
let reconnectAttempts = 0;

const MAX_RECONNECT_DELAY = 60000;
const HEARTBEAT_INTERVAL = 25000;

function log(message: string) {
  if (term) {
    term.writeln(`\x1b[90m[${new Date().toLocaleTimeString()}] ${message}\x1b[0m`);
  }
}

function submitToken() {
  const value = loginInput.value.trim();
  if (!value) return;
  token.value = value;
  localStorage.setItem(TOKEN_STORAGE_KEY, value);
  needsLogin.value = false;
  loginError.value = '';
  loginInput.value = '';
  connect();
}

// ---- SaaS 手机号登录（短信验证码 → relay 租户票据） ----
const loginMode = ref<'phone' | 'token'>('phone');
const phoneInput = ref('');
const codeInput = ref('');
const smsSending = ref(false);
const smsCountdown = ref(0);
let smsTimer: number | null = null;

async function sendLoginSms() {
  const phone = phoneInput.value.trim();
  if (!/^1\d{10}$/.test(phone)) {
    loginError.value = '手机号格式不对';
    return;
  }
  smsSending.value = true;
  loginError.value = '';
  try {
    const r = await fetch(CONSOLE_BASE + '/api/sms/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || '短信没发出去，过一会儿再试');
    smsCountdown.value = 60;
    if (smsTimer) clearInterval(smsTimer);
    smsTimer = window.setInterval(() => {
      smsCountdown.value -= 1;
      if (smsCountdown.value <= 0 && smsTimer) clearInterval(smsTimer);
    }, 1000);
  } catch (err: any) {
    loginError.value = err?.message || '短信没发出去，过一会儿再试';
  } finally {
    smsSending.value = false;
  }
}

async function submitPhoneLogin() {
  const phone = phoneInput.value.trim();
  const code = codeInput.value.trim();
  if (!phone || !code) return;
  loginError.value = '';
  try {
    const r = await fetch(CONSOLE_BASE + '/api/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, code }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || '登录没成功，请再试一次');
    if (!j.relayTicket) throw new Error('登录回话里缺票据，请再试一次');
    token.value = j.relayTicket;
    localStorage.setItem(TOKEN_STORAGE_KEY, j.relayTicket);
    needsLogin.value = false;
    phoneInput.value = '';
    codeInput.value = '';
    connect();
  } catch (err: any) {
    loginError.value = err?.message || '登录没成功，请再试一次';
  }
}

function closeWsSilently() {
  if (!ws) return;
  const sock = ws;
  ws = null;
  sock.onclose = null;
  sock.onerror = null;
  sock.close();
}

function logout() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  token.value = '';
  needsLogin.value = true;
  closeWsSilently();
}

function connect() {
  if (needsLogin.value) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  statusText.value = '连接中...';
  log(`正在连接中继服务器: ${RELAY_URL}`);

  try {
    // 实例比对：旧连接的回调晚到时不得清除新连接的状态（logout/AUTH_FAILED 后立即重连场景）
    const sock = new WebSocket(RELAY_URL);
    ws = sock;

    sock.onopen = () => {
      if (ws !== sock) return;
      connected.value = true;
      reconnectAttempts = 0;
      lastPongAt = Date.now();
      statusText.value = '已连接，认证中...';
      log('已连接到中继服务器');
      authenticate();
      startHeartbeat();
    };

    sock.onmessage = (event) => {
      if (ws !== sock) return;
      handleMessage(event.data);
    };

    sock.onclose = () => {
      if (ws !== sock) return;
      connected.value = false;
      authenticated.value = false;
      agentOnline.value = false;
      ptyActive.value = false;
      ptyOpening = false;
      clearPtyOpenTimer();
      statusText.value = '已断开';
      log('连接已断开');
      cleanup();
      scheduleReconnect();
    };

    sock.onerror = (err) => {
      if (ws !== sock) return;
      console.error('WebSocket error:', err);
      log('连接错误');
    };
  } catch (err) {
    console.error('Failed to connect:', err);
    scheduleReconnect();
  }
}

function authenticate() {
  if (!ws || ws.readyState !== WebSocket.OPEN) return;

  const authMsg = createMessage(MessageType.AUTH_REQUEST, {
    token: token.value,
    deviceId: DEVICE_ID,
    deviceType: 'client',
  });

  send(authMsg);
}

let lastPongAt = 0;

function startHeartbeat() {
  if (heartbeatTimer) clearInterval(heartbeatTimer);

  heartbeatTimer = window.setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      // 活性检测：连续两次心跳无 PONG = 僵尸半开连接（锁屏/切后台冻结），主动断触发重连
      if (lastPongAt && Date.now() - lastPongAt > HEARTBEAT_INTERVAL * 2 + 5000) {
        log('心跳无响应，主动重连');
        ws.close();
        return;
      }
      send(createMessage(MessageType.PING, { timestamp: Date.now() }));
    }
  }, HEARTBEAT_INTERVAL);
}

function onVisibilityChange() {
  if (document.visibilityState !== 'visible' || needsLogin.value) return;
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    connect();
    return;
  }
  // 回前台立即探活：3 秒内无 PONG 视为僵尸连接
  const probeStart = Date.now();
  send(createMessage(MessageType.PING, { timestamp: Date.now() }));
  window.setTimeout(() => {
    if (ws && ws.readyState === WebSocket.OPEN && lastPongAt < probeStart) {
      log('回前台探活失败，主动重连');
      ws.close();
    }
  }, 3000);
}

function onPageShow(e: PageTransitionEvent) {
  // bfcache 恢复：pagehide 已杀了 PTY，必须重开，否则是外观正常的死终端
  if (e.persisted && !needsLogin.value && view.value === 'terminal') {
    ptyActive.value = false;
    openPty(true);
  }
}

function scheduleReconnect() {
  if (reconnectTimer) clearTimeout(reconnectTimer);

  const delay = Math.min(
    5000 * Math.pow(1.5, reconnectAttempts),
    MAX_RECONNECT_DELAY
  );

  reconnectAttempts++;
  log(`${delay / 1000}秒后重连 (第${reconnectAttempts}次)`);

  reconnectTimer = window.setTimeout(() => {
    connect();
  }, delay);
}

function cleanup() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  ws = null;
}

function send(message: BaseMessage): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    log('无法发送消息：未连接');
    return false;
  }

  try {
    // 多电脑选台：选定电脑后，所有出向消息带目标（auth 除外）；relay 消费后不下发
    const out =
      currentDeviceId.value && message.type !== MessageType.AUTH_REQUEST
        ? { ...message, targetDeviceId: currentDeviceId.value }
        : message;
    ws.send(JSON.stringify(out));
    return true;
  } catch (err) {
    console.error('Failed to send:', err);
    return false;
  }
}

// ---- 多电脑选台 ----
interface Computer {
  deviceId: string;
  online: boolean;
  lastSeen: number;
}
const computers = ref<Map<string, Computer>>(new Map());
const currentDeviceId = ref(localStorage.getItem('yuzai-device') || '');
const onlineComputers = computed(() => [...computers.value.values()].filter((c) => c.online));

function noteComputer(deviceId: string | undefined, online: boolean) {
  if (!deviceId || deviceId.startsWith('client-')) return;
  const m = new Map(computers.value);
  m.set(deviceId, { deviceId, online, lastSeen: Date.now() });
  computers.value = m;
  // 选中的电脑下线了 → 清除选择（回落主电脑）
  if (!online && currentDeviceId.value === deviceId) {
    currentDeviceId.value = '';
    localStorage.removeItem('yuzai-device');
  }
}

function deviceLabel(deviceId: string): string {
  return deviceId.replace(/^yza-/, '') || deviceId;
}

function pickComputer(deviceId: string) {
  currentDeviceId.value = deviceId;
  if (deviceId) localStorage.setItem('yuzai-device', deviceId);
  else localStorage.removeItem('yuzai-device');
  // 换电脑：PTy 在旧电脑上，重开到新电脑；项目列表也从新电脑拉
  if (view.value === 'terminal') openPty(true);
  if (view.value === 'projects') refreshProjects();
}

function handleMessage(data: string) {
  try {
    const raw = JSON.parse(data);
    if (!isValidMessage(raw)) return;

    const message = raw as BaseMessage;

    // 多电脑选台：agent 消息带来源标识，据此维护在线电脑名册
    if (message.fromDeviceId) noteComputer(message.fromDeviceId, true);

    switch (message.type) {
      case MessageType.AUTH_RESPONSE: {
        const p = message.payload as { success?: boolean };
        if (p && p.success === false) break;
        authenticated.value = true;
        statusText.value = '已认证';
        log('认证成功');
        if (view.value === 'terminal') {
          openPty();
        } else if (view.value === 'projects') {
          refreshProjects();
        } else if (view.value === 'bridge') {
          // 重连后恢复桌面会话订阅：agent 侧 watcher 会补发历史，客户端先清空防重复
          if (bridgeMode.value === 'chat' && activeSession.value) {
            bridgeEvents.value = [];
            send(createMessage(MessageType.BRIDGE_WATCH, { sessionId: activeSession.value.sessionId }));
          } else {
            refreshSessions();
          }
        }
        break;
      }

      case MessageType.PTY_OPENED:
        handlePtyOpened(message);
        break;

      case MessageType.PTY_OUTPUT:
        handlePtyOutput(message);
        break;

      case MessageType.PTY_CLOSED:
        handlePtyClosed(message);
        break;

      case MessageType.NOTIFY_EVENT: {
        const p = message.payload as NotifyEventPayload;
        log(`任务完成: ${p.project ? p.project + ' / ' : ''}${p.title}`);
        break;
      }

      case MessageType.STATUS_UPDATE:
        handleStatusUpdate(message);
        break;

      case MessageType.ERROR:
        handleError(message);
        break;

      case MessageType.PONG:
        lastPongAt = Date.now();
        break;

      case MessageType.BRIDGE_SESSION_LIST:
        bridgeSessions.value = (message.payload as BridgeSessionInfo[]) || [];
        break;

      case MessageType.PROJECT_LIST_RESULT:
        projects.value = (message.payload as ProjectListResultPayload)?.projects || [];
        projectsLoading.value = false;
        projectsError.value = ''; // 有结果即清掉"没有在线电脑"提示
        break;

      case MessageType.PROJECT_FOCUS_RESULT: {
        const r = message.payload as ProjectFocusResultPayload;
        if (r.ok) {
          if (r.paneId) focusedPaneId.value = r.paneId;
          openTerminalView();
        } else {
          projectsError.value = r.error || '切换失败';
        }
        break;
      }

      // ---- 点选开/关项目回执（五期·十九） ----
      case MessageType.FOLDER_LIST_RES: {
        const r = message.payload as FolderListResPayload;
        if (folderPicker.value) folderPicker.value = { loading: false, folders: r.folders || [], failedRoots: r.failedRoots || [] };
        break;
      }

      case MessageType.OPEN_PROJECT_RESULT: {
        const r = message.payload as OpenProjectResultPayload;
        folderOpening.value = '';
        if (r.ok) {
          folderPicker.value = null;
          chatMessages.value.push({ kind: 'tool', text: `✅ 项目「${r.project}」开好了，AI 已经在里面就位。`, ts: Date.now() });
          if (r.paneId) focusedPaneId.value = r.paneId; // 指代链：开完即聚焦，"这个项目"就指它
          refreshProjects();
        } else {
          chatMessages.value.push({ kind: 'error', text: r.error || '没开成。', ts: Date.now() });
        }
        scrollChatPanel(true);
        break;
      }

      case MessageType.PROJECT_CLOSE_RESULT: {
        const r = message.payload as ProjectCloseResultPayload;
        chatMessages.value.push(
          r.ok
            ? { kind: 'tool', text: '项目关好了。存档都在磁盘上，想再开跟我说。', ts: Date.now() }
            : { kind: 'error', text: `没关成：${r.error || '未知原因'}`, ts: Date.now() }
        );
        if (r.ok && focusedPaneId.value === r.paneId) focusedPaneId.value = ''; // 指代链别挂着已关的窗格
        refreshProjects();
        scrollChatPanel(true);
        break;
      }

      case MessageType.APP_DEBUG_CMD:
        handleDebugCmd(message.payload as AppDebugCmdPayload);
        break;

      case MessageType.PLUGINS_LIST_RESULT:
        pluginsList.value = (message.payload as { plugins?: PluginItem[] })?.plugins || [];
        pluginsLoading.value = false;
        break;

      case MessageType.PLUGINS_SELFTEST_RESULT: {
        const r = message.payload as { name: string; ok: boolean; detail: string };
        pluginTestResult.value = { ...pluginTestResult.value, [r.name]: { ok: r.ok, detail: r.detail } };
        break;
      }

      case MessageType.TASK_CARD: {
        const c = message.payload as TaskCardPayload;
        // 汇报卡片直接进对话流（它本来就是"总监对你说的话"，不再单占屏幕区域）
        chatMessages.value.push({
          kind: 'card',
          text: c.oneLine,
          project: c.project,
          detail: c.detail,
          suggestion: c.suggestion,
          cardKind: c.kind,
          ts: c.ts || Date.now(),
        });
        if (chatMessages.value.length > 200) chatMessages.value.splice(0, 50);
        scrollChatPanel();
        // 卡片到达不单独响铃——响铃统一由待查看闹铃节奏驱动
        speakAuto(`${c.project}，${c.oneLine}`);
        break;
      }

      case MessageType.CHAT_EVENT:
        handleChatEvent(message.payload as ChatEventPayload);
        break;

      case MessageType.ATTENTION_RING:
        handleAttentionRing(message.payload as AttentionRingPayload);
        break;

      case MessageType.ACTIVITY_TICK:
        handleActivityTick(message.payload as ActivityTickPayload);
        break;

      case MessageType.BRIDGE_EVENT: {
        const ev = message.payload as BridgeEventPayload;
        if (!activeSession.value || ev.sessionId !== activeSession.value.sessionId) break;
        bridgeEvents.value.push(ev);
        if (bridgeEvents.value.length > 2000) bridgeEvents.value.splice(0, 500);
        scrollChatToBottom();
        break;
      }

      case MessageType.BRIDGE_SEND_RESULT: {
        const r = message.payload as BridgeSendResultPayload;
        if (!r.ok) bridgeSendError.value = r.error || '发送失败';
        break;
      }
    }
  } catch (err) {
    console.error('Error handling message:', err);
  }
}

// ---- 任务完成铃声（WebAudio，无需音频资源；首次手势后解锁） ----
let audioCtx: AudioContext | null = null;

// ---- 朗读（APP 内走原生 TTS 桥——安卓 WebView 的 Web Speech 是残的；浏览器用 Web Speech 免费方案） ----
const ttsOn = ref(localStorage.getItem('yuzai-tts') === '1');
let zhVoice: SpeechSynthesisVoice | null = null;

interface YzTtsNative {
  isAvailable(): boolean;
  speak(text: string): void;
  stop(): void;
}
function nativeTts(): YzTtsNative | null {
  const t = (window as unknown as { YuzaiTts?: YzTtsNative }).YuzaiTts;
  if (!t) return null;
  try {
    return t.isAvailable() ? t : null;
  } catch {
    return null;
  }
}
let ttsEndCb: (() => void) | null = null;
(window as unknown as Record<string, unknown>).__yzTtsEvent = (ev: { type: string }) => {
  if (ev.type === 'end' && ttsEndCb) {
    const cb = ttsEndCb;
    ttsEndCb = null;
    try {
      cb();
    } catch {
      // 忽略
    }
  }
};

function pickZhVoice() {
  try {
    const voices = window.speechSynthesis?.getVoices() || [];
    zhVoice = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith('zh')) || null;
  } catch {
    zhVoice = null;
  }
}

function speak(text: string, onend?: () => void) {
  // APP 壳里优先原生 TTS（Web Speech 在安卓 WebView 静默失败，真机踩坑）
  const nt = nativeTts();
  if (nt) {
    try {
      ttsEndCb = onend || null;
      nt.stop(); // 先停旧的，不重叠
      nt.speak(text);
      if (!onend) ttsEndCb = null;
      return;
    } catch {
      // 落到 Web Speech 兜底
    }
  }
  if (!window.speechSynthesis) {
    onend?.();
    return;
  }
  try {
    window.speechSynthesis.cancel(); // 先停旧的，不重叠
    const u = new SpeechSynthesisUtterance(text);
    if (zhVoice) u.voice = zhVoice;
    u.lang = zhVoice?.lang || 'zh-CN';
    u.rate = 1.05;
    if (onend) {
      u.onend = () => onend();
      u.onerror = () => onend(); // 出错也要收尾，别让对讲状态机卡住
    }
    window.speechSynthesis.speak(u);
  } catch {
    // 静默失败（朗读是增强，不碍主流程）
    onend?.();
  }
}

/** 点读（任何消息旁的 🔊） */
function speakOnce(text: string) {
  speak(text);
}

/** 自动朗读（开关开着时，新总管回复/新卡片触发） */
function speakAuto(text: string) {
  if (!ttsOn.value) return;
  speak(text);
}

function toggleTts() {
  ttsOn.value = !ttsOn.value;
  localStorage.setItem('yuzai-tts', ttsOn.value ? '1' : '0');
  if (!ttsOn.value) {
    nativeTts()?.stop();
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  }
}

// ---- 语音对讲（五期·十七：语音只是耳朵和嘴，大脑不动——识别出的文字走 CHAT_SEND，与打字完全同路） ----
const voiceMode = ref(localStorage.getItem('yuzai-voice') === '1');
const voiceState = ref<'idle' | 'listening' | 'thinking' | 'speaking'>('idle');
const voiceHint = ref('');
let pendingVoiceConfirmId: string | null = null;

interface YzVoiceNative {
  isAvailable(): boolean;
  start(): void;
  stop(): void;
  cancel(): void;
}
interface WebkitRec {
  stop(): void;
  abort(): void;
}
let webkitRec: WebkitRec | null = null;

function nativeVoice(): YzVoiceNative | null {
  const v = (window as unknown as { YuzaiVoice?: YzVoiceNative }).YuzaiVoice;
  if (!v) return null;
  try {
    return v.isAvailable() ? v : null;
  } catch {
    return null;
  }
}

/** 点🎙：听音中=手动收口；否则开听（并记住对讲模式） */
function tapMic() {
  if (voiceState.value === 'listening') {
    if (mediaStream) stopCloudAndSend();
    else stopListening();
    return;
  }
  voiceMode.value = true;
  localStorage.setItem('yuzai-voice', '1');
  startListening();
}

function startListening() {
  if (chatBusy.value) return;
  const nv = nativeVoice();
  if (nv) {
    voiceState.value = 'listening';
    voiceHint.value = '';
    nv.start();
    return;
  }
  const w = window as unknown as Record<string, unknown>;
  const SR = (w.webkitSpeechRecognition || w.SpeechRecognition) as (new () => unknown) | undefined;
  if (SR && !navigator.mediaDevices?.getUserMedia) {
    // 没有云耳朵条件才用 webkit 兜底（国内安卓 WebView 的 webkit 识别走 Google 基本残废，云优先）
    try {
      const rec = new SR() as {
        lang: string;
        interimResults: boolean;
        onresult: ((e: { results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
        onerror: (() => void) | null;
        onend: (() => void) | null;
        start(): void;
        stop(): void;
        abort(): void;
      };
      rec.lang = 'zh-CN';
      rec.interimResults = true;
      rec.onresult = (e) => {
        let finalText = '';
        let interim = '';
        for (let i = 0; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) finalText += r[0].transcript;
          else interim += r[0].transcript;
        }
        if (interim) {
          chatInput.value = interim;
          autoGrowChatInput();
        }
        if (finalText) handleVoiceText(finalText);
      };
      rec.onerror = () => asrEndedQuietly();
      rec.onend = () => asrEndedQuietly();
      rec.start();
      webkitRec = rec;
      voiceState.value = 'listening';
      voiceHint.value = '';
      return;
    } catch {
      webkitRec = null;
    }
  }
  startCloudListening();
}

function stopListening() {
  const nv = nativeVoice();
  if (nv) nv.stop();
  else if (webkitRec) webkitRec.stop();
  // 识别器随后会吐最终结果 → handleVoiceText
}

/** 没听清/超时（默认十几秒没说话）= 自动收 */
function asrEndedQuietly() {
  if (voiceState.value !== 'listening') return;
  voiceState.value = 'idle';
  if (voiceMode.value) voiceHint.value = '没听清，点🎙再说';
}

/** 原生桥回调（VoiceBridge.java 注入） */
(window as unknown as Record<string, unknown>).__yzVoiceEvent = (ev: { type: string; text?: string; code?: number }) => {
  if (ev.type === 'partial' && ev.text) {
    chatInput.value = ev.text;
    autoGrowChatInput();
  } else if (ev.type === 'final') {
    handleVoiceText(ev.text || '');
  } else if (ev.type === 'error') {
    // 6/7=静默超时/没听清（识别服务是好的）；其余=服务坏了（H14：厂商拒客/断网）→ 自动换云耳朵
    if (ev.code === 6 || ev.code === 7) asrEndedQuietly();
    else startCloudListening();
  }
};

// ---- 云耳朵（五期·十七·五：系统识别死局（H14）后的主力耳朵——PCM 16k 直传 console 一句话识别，与手机品牌无关） ----
let mediaStream: MediaStream | null = null;
let cloudAudioCtx: AudioContext | null = null;
let pcmChunks: Int16Array[] = [];
let cloudStopTimer: number | null = null;
let heardSound = false;
let lastSoundAt = 0;
let cloudBusy = false;

async function startCloudListening() {
  if (chatBusy.value || cloudBusy || voiceState.value === 'thinking') return;
  if (!navigator.mediaDevices?.getUserMedia) {
    voiceHint.value = '这台设备开不了麦克风，用输入法自带的语音输入吧';
    voiceState.value = 'idle';
    return;
  }
  cloudBusy = true;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    mediaStream = stream;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctx();
    cloudAudioCtx = ctx;
    const src = ctx.createMediaStreamSource(stream);
    const proc = ctx.createScriptProcessor(4096, 1, 1);
    pcmChunks = [];
    heardSound = false;
    lastSoundAt = 0;
    const ratio = ctx.sampleRate / 16000;
    proc.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      let sum = 0;
      for (let i = 0; i < input.length; i += 16) sum += input[i] * input[i];
      const rms = Math.sqrt(sum / (input.length / 16));
      const now = Date.now();
      if (rms > 0.015) {
        heardSound = true;
        lastSoundAt = now;
      }
      const out = new Int16Array(Math.floor(input.length / ratio));
      for (let i = 0; i < out.length; i++) {
        const s = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)]));
        out[i] = s * 0x7fff;
      }
      pcmChunks.push(out);
      if (heardSound && now - lastSoundAt > 2500) stopCloudAndSend(); // 说完话静默 2.5s 自动收口
    };
    src.connect(proc);
    proc.connect(ctx.destination);
    voiceState.value = 'listening';
    voiceHint.value = '';
    // 15s 无人声自动收；30s 封顶
    cloudStopTimer = window.setTimeout(() => {
      if (voiceState.value !== 'listening') return;
      if (heardSound) stopCloudAndSend();
      else {
        cleanupCloud();
        asrEndedQuietly();
      }
    }, 30000);
    window.setTimeout(() => {
      if (voiceState.value === 'listening' && !heardSound) {
        cleanupCloud();
        asrEndedQuietly();
      }
    }, 15000);
  } catch {
    voiceHint.value = '麦克风没开成（权限被挡了？），用输入法自带的语音输入吧';
    voiceState.value = 'idle';
  } finally {
    cloudBusy = false;
  }
}

function cleanupCloud() {
  if (cloudStopTimer) {
    clearTimeout(cloudStopTimer);
    cloudStopTimer = null;
  }
  try {
    cloudAudioCtx?.close();
  } catch {
    // 忽略
  }
  cloudAudioCtx = null;
  if (mediaStream) {
    mediaStream.getTracks().forEach((t) => t.stop());
    mediaStream = null;
  }
}

async function stopCloudAndSend() {
  if (voiceState.value !== 'listening') return;
  const chunks = pcmChunks;
  cleanupCloud();
  const total = chunks.reduce((s, c) => s + c.length, 0);
  if (total < 16000 * 0.5) {
    asrEndedQuietly(); // 太短（<0.5s）当没说
    return;
  }
  const pcm = new Int16Array(total);
  let off = 0;
  for (const c of chunks) {
    pcm.set(c, off);
    off += c.length;
  }
  voiceState.value = 'thinking';
  const bytes = new Uint8Array(pcm.buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  try {
    const r = await fetch(CONSOLE_BASE + '/api/asr/once', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token.value}` },
      body: JSON.stringify({ audio: btoa(bin), format: 'pcm', sampleRate: 16000 }),
    });
    const j = (await r.json()) as { ok?: boolean; text?: string };
    if (j.ok && typeof j.text === 'string') {
      handleVoiceText(j.text);
      return;
    }
    throw new Error('asr failed');
  } catch {
    voiceHint.value = '这会儿没听清，点🎙再说';
    voiceState.value = 'idle';
  }
}

function handleVoiceText(raw: string) {
  const text = raw.trim();
  chatInput.value = '';
  nextTick(() => {
    if (chatInputEl.value) chatInputEl.value.style.height = 'auto';
  });
  if (!text) {
    asrEndedQuietly();
    return;
  }
  // 语音确认门：有待确认时先走固定词快通道（不过模型，放心开关同思想）
  if (pendingVoiceConfirmId) {
    const verdict = matchVoiceConfirm(text);
    if (verdict) {
      const id = pendingVoiceConfirmId;
      pendingVoiceConfirmId = null;
      answerConfirm(id, verdict === 'approve');
      voiceState.value = 'speaking';
      speak(verdict === 'approve' ? '好，执行了' : '好，取消了', voiceLoopContinue);
      return;
    }
    // 没命中固定词：不猜，当普通对话发给总监（确认按钮仍在界面上，不耽误）
  }
  if (chatBusy.value) {
    voiceState.value = 'thinking';
    return;
  }
  voiceState.value = 'thinking';
  chatInput.value = text;
  sendChat();
}

/** 总监说完一句 → 对讲还开着就自动继续听（对话循环）；用户点✕退出才停 */
function voiceLoopContinue() {
  if (!voiceMode.value) {
    voiceState.value = 'idle';
    return;
  }
  voiceState.value = 'idle';
  startListening();
}

function exitVoice() {
  voiceMode.value = false;
  localStorage.setItem('yuzai-voice', '0');
  pendingVoiceConfirmId = null;
  const nv = nativeVoice();
  if (nv) nv.cancel();
  else if (webkitRec) webkitRec.abort();
  cleanupCloud();
  nativeTts()?.stop();
  if (window.speechSynthesis) window.speechSynthesis.cancel();
  voiceState.value = 'idle';
  voiceHint.value = '';
}

const voiceStateText = computed(() => {
  if (voiceHint.value) return voiceHint.value;
  switch (voiceState.value) {
    case 'listening':
      return pendingVoiceConfirmId ? '等你的口令：确认 / 取消' : '正在听，说吧…（点🎙收口）';
    case 'thinking':
      return '总监在想…';
    case 'speaking':
      return '总监在说…（说完自动继续听）';
    default:
      return '点🎙说话，我听着';
  }
});

function ensureAudioCtx() {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    } catch {
      audioCtx = null;
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
}

function playBell() {
  ensureAudioCtx();
  if (!audioCtx) return;
  const ctx = audioCtx;
  // 双音三响：880Hz ↔ 1318Hz 交替三次，穿透力强
  const start = ctx.currentTime + 0.02;
  for (let i = 0; i < 3; i++) {
    const t0 = start + i * 0.45;
    for (const [freq, offset] of [[880, 0], [1318.5, 0.16]] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t0 + offset);
      gain.gain.exponentialRampToValueAtTime(0.5, t0 + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + offset + 0.14);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + offset);
      osc.stop(t0 + offset + 0.16);
    }
  }
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** 将单个字符转换为 Ctrl 组合的控制码 */
function applyCtrl(data: string): string {
  if (data.length !== 1) return data;
  const c = data.charCodeAt(0);
  if (c >= 97 && c <= 122) return String.fromCharCode(c - 96); // a-z
  const map: Record<string, number> = { '@': 0, '[': 27, '\\': 28, ']': 29, '^': 30, '_': 31, '?': 127 };
  if (data in map) return String.fromCharCode(map[data]);
  return data;
}

function clearPtyOpenTimer() {
  if (ptyOpenTimer) {
    clearTimeout(ptyOpenTimer);
    ptyOpenTimer = null;
  }
}

function openPty(force = false) {
  if (!term || !authenticated.value || !ws || ws.readyState !== WebSocket.OPEN) return;
  if (ptyOpening && !force) return; // 已有打开请求在途（AUTH_RESPONSE 与 Agent 上线通知都会触发）
  if (force) ptyRetryCount = 0;
  if (ptyRetryTimer) {
    clearTimeout(ptyRetryTimer);
    ptyRetryTimer = null;
  }
  // ptyId 在页面生命周期内保持稳定：断线重连用同一 id 重开，
  // agent 侧同 id 先关后开，旧 tmux-client 进程不会成孤儿
  if (!ptyId) ptyId = `pty-${Math.random().toString(36).slice(2, 11)}`;
  ptyActive.value = false;
  ptyOpening = true;
  const sent = send(createMessage<PtyOpenPayload>(MessageType.PTY_OPEN, {
    ptyId,
    cols: term.cols,
    rows: term.rows,
    cmd: PTY_CMD,
  }));
  if (!sent) {
    ptyOpening = false;
    schedulePtyRetry('发送失败');
    return;
  }
  // 打开超时兜底：agent 离线/丢包时收不到 OPENED，必须给状态机留失败出口
  clearPtyOpenTimer();
  ptyOpenTimer = window.setTimeout(() => {
    ptyOpenTimer = null;
    if (ptyOpening && !ptyActive.value) {
      ptyOpening = false;
      schedulePtyRetry('打开超时');
    }
  }, PTY_OPEN_TIMEOUT);
}

function schedulePtyRetry(reason: string) {
  if (ptyRetryCount >= PTY_MAX_RETRY) {
    term?.writeln(`\r\n\x1b[33m[终端未连接：${reason}，点下方 ⟳ 重试]\x1b[0m`);
    return;
  }
  if (ptyRetryTimer) return; // 已有重连计时器，不重复武装
  ptyRetryCount++;
  term?.writeln(`\x1b[90m[${reason}，2秒后重连（第${ptyRetryCount}次）…]\x1b[0m`);
  ptyRetryTimer = window.setTimeout(() => {
    ptyRetryTimer = null;
    openPty();
  }, 2000);
}

function handlePtyOpened(message: BaseMessage) {
  const p = message.payload as PtyOpenedPayload;
  if (p.ptyId !== ptyId) return;
  ptyOpening = false;
  clearPtyOpenTimer();
  if (p.ok) {
    ptyActive.value = true;
    ptyRetryCount = 0;
    if (ptyRetryTimer) {
      clearTimeout(ptyRetryTimer);
      ptyRetryTimer = null;
    }
    // 有等待中的项目聚焦：终端连上后补发
    if (pendingFocusPaneId) {
      const paneId = pendingFocusPaneId;
      pendingFocusPaneId = '';
      send(createMessage(MessageType.PROJECT_FOCUS, { paneId, ptyId }));
    }
    if (view.value === 'terminal') term?.focus(); // bridge 页期间不得抢焦弹键盘
  } else {
    ptyActive.value = false;
    term?.writeln(`\x1b[31m[终端打开失败] ${p.error || '未知错误'}\x1b[0m`);
    schedulePtyRetry(p.error || '打开失败');
  }
}

function handlePtyOutput(message: BaseMessage) {
  const p = message.payload as PtyOutputPayload;
  if (p.ptyId !== ptyId || !term) return;
  term.write(b64ToBytes(p.data));
}

function handlePtyClosed(message: BaseMessage) {
  const p = message.payload as PtyClosedPayload;
  if (p.ptyId !== ptyId) return;
  // 打开进行中的 CLOSED 不处理：同 id 重开/agent 逐出等交接场景下，
  // 旧会话的关闭通知不应打断正在建立的新会话（agent 侧同 id 重开已不再补发 CLOSED，此为防御）
  if (ptyOpening) return;
  ptyActive.value = false;
  schedulePtyRetry(`终端已退出（code ${p.exitCode ?? '-'}）`);
}

/** 功能键栏：直接向 PTY 发送原始序列，不经过 Ctrl 转换。
 *  只在 click 时发送（pointerdown 仅 prevent 防抢焦）——
 *  横滑浏览按钮时滚动会取消 click，不会误发按键（滑过 ⌃C 不会 SIGINT） */
function sendFnKey(data: string) {
  ctrlArmed.value = false;
  if (!ptyActive.value) {
    openPty(true);
    return;
  }
  send(createMessage(MessageType.PTY_INPUT, { ptyId, data }));
  term?.focus();
}

function toggleCtrl() {
  ctrlArmed.value = !ctrlArmed.value;
  term?.focus();
}

function handleStatusUpdate(message: BaseMessage) {
  const payload = message.payload as StatusPayload;

  // agent 的 deviceId 由部署侧定义（如 mac-agent-01），用排除法判定：非本客户端即 agent
  if (!payload.deviceId.startsWith('client-')) {
    noteComputer(payload.deviceId, payload.online);
    const wasOnline = agentOnline.value;
    agentOnline.value = payload.online;
    statusText.value = payload.online ? 'Agent 在线' : 'Agent 离线';
    log(`Agent ${payload.online ? '上线' : '离线'}: ${payload.deviceId}`);
    if (!payload.online) {
      // agent 下线意味着其持有的 PTY 全部死亡（未必来得及发 PTY_CLOSED）
      ptyActive.value = false;
    } else if (!wasOnline && view.value === 'terminal') {
      // agent 重启归来：自动重开 PTY attach 回 tmux
      openPty();
    }
  }
}

function handleError(message: BaseMessage) {
  const payload = message.payload as { code: string; message: string };

  if (payload.code === 'AUTH_FAILED') {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    token.value = '';
    needsLogin.value = true;
    loginError.value = 'Token 无效，请重新输入';
    closeWsSilently();
    return;
  }

  term?.writeln(`\r\n\x1b[31m[错误] ${payload.code}: ${payload.message}\x1b[0m`);

  // 空租户/无 agent 在线（H16）：项目列表的 NO_PEER 要如实落地，不许永远"加载中"
  if (payload.code === 'NO_PEER') {
    projectsLoading.value = false;
    if (view.value === 'projects' && !projects.value.length) {
      projectsError.value = isSaaS.value
        ? '这个账号下还没有电脑——在电脑上打开 console.ymgymg.xin 登录后点「安装新电脑」，装好这里就有项目了。'
        : '没有找到在线的电脑——电脑上的语崽智脑 agent 可能停了，去电脑上看看。';
    }
  }

  // agent 不在线：打开请求必然失败，复位状态机并走重试（否则 ptyOpening 卡死）
  if (payload.code === 'NO_PEER' && ptyOpening) {
    ptyOpening = false;
    clearPtyOpenTimer();
    schedulePtyRetry('Agent 不在线');
  }
}

function initTerminal() {
  if (!terminalRef.value) return;

  term = new Terminal({
    cursorBlink: true,
    fontSize: 14,
    fontFamily: 'Menlo, Monaco, "Courier New", monospace',
    theme: {
      background: '#1e1e1e',
      foreground: '#d4d4d4',
      cursor: '#d4d4d4',
      black: '#000000',
      red: '#cd3131',
      green: '#0dbc79',
      yellow: '#e5e510',
      blue: '#2472c8',
      magenta: '#bc3fbc',
      cyan: '#11a8cd',
      white: '#e5e5e5',
      brightBlack: '#666666',
      brightRed: '#f14c4c',
      brightGreen: '#23d18b',
      brightYellow: '#f5f543',
      brightBlue: '#3b8eea',
      brightMagenta: '#d670d6',
      brightCyan: '#29b8db',
      brightWhite: '#e5e5e5',
    },
  });

  fitAddon = new FitAddon();
  term.loadAddon(fitAddon);
  term.open(terminalRef.value);
  fitAddon.fit();

  term.writeln('\x1b[1;32m╔══════════════════════════════════════╗\x1b[0m');
  term.writeln('\x1b[1;32m║   Kimi Remote Terminal (tmux main)  ║\x1b[0m');
  term.writeln('\x1b[1;32m╚══════════════════════════════════════╝\x1b[0m');
  term.writeln('');

  // 全键转发：所有按键原样发给 PTY（Ctrl 武装时转换控制码）
  term.onData((data) => {
    if (!ptyActive.value) return;
    if (ctrlArmed.value) {
      data = applyCtrl(data);
      ctrlArmed.value = false;
    }
    send(createMessage(MessageType.PTY_INPUT, { ptyId, data }));
  });

  // 尺寸变化同步给 PTY
  term.onResize(({ cols, rows }) => {
    if (ptyActive.value) {
      send(createMessage(MessageType.PTY_RESIZE, { ptyId, cols, rows }));
    }
  });

  // Handle resize（含手机软键盘弹起：visualViewport 才会变）
  onWinResize = () => {
    const vv = window.visualViewport;
    // iOS 键盘弹起时 layout viewport 不收缩，fn-bar 会被键盘盖住；
    // 把可视高度写进 CSS 变量，让容器跟着键盘收缩
    if (vv) document.documentElement.style.setProperty('--vvh', `${vv.height}px`);
    if (view.value !== 'terminal') return;
    fitAddon?.fit();
  };
  window.addEventListener('resize', onWinResize);
  window.visualViewport?.addEventListener('resize', onWinResize);
}

onMounted(() => {
  initTerminal();
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  document.addEventListener('visibilitychange', onVisibilityChange);
  // 首次手势解锁音频（浏览器自动播放策略）
  window.addEventListener('pointerdown', ensureAudioCtx, { passive: true });
  pickZhVoice();
  window.speechSynthesis?.addEventListener?.('voiceschanged', pickZhVoice);
  startProjectsTimer(); // 默认页是项目总览
  startMonitorCheck();
  scrollTop();
  if (!needsLogin.value) {
    connect();
  }
});

onUnmounted(() => {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  if (ptyRetryTimer) clearTimeout(ptyRetryTimer);
  clearPtyOpenTimer();
  stopProjectsTimer();
  stopMonitorCheck();
  window.removeEventListener('pagehide', onPageHide);
  window.removeEventListener('pageshow', onPageShow);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  if (onWinResize) {
    window.removeEventListener('resize', onWinResize);
    window.visualViewport?.removeEventListener('resize', onWinResize);
  }
  if (ptyId) send(createMessage(MessageType.PTY_CLOSE, { ptyId }));
  if (ws) ws.close();
  if (term) term.dispose();
});
</script>

<template>
  <div class="container" :class="{ 'monitor-dead': monitorDead }">
    <div v-if="needsLogin" class="login-overlay">
      <div class="login-hero">
        <img class="login-logo" src="/logo.svg" alt="语崽智脑" />
        <h1 class="brand-name">语崽智脑</h1>
        <p class="brand-tagline">你的 AI 军团 · 一屏统管</p>
        <div class="login-tabs">
          <button :class="{ cur: loginMode === 'phone' }" @click="loginMode = 'phone'; loginError = ''">手机号登录</button>
          <button :class="{ cur: loginMode === 'token' }" @click="loginMode = 'token'; loginError = ''">令牌登录</button>
        </div>
        <template v-if="loginMode === 'phone'">
          <input v-model="phoneInput" type="tel" maxlength="11" placeholder="手机号" autofocus />
          <div class="login-code-row">
            <input
              v-model="codeInput"
              type="tel"
              maxlength="6"
              placeholder="短信验证码"
              @keyup.enter="!$event.isComposing && submitPhoneLogin()"
            />
            <button class="sms-btn" :disabled="smsSending || smsCountdown > 0" @click="sendLoginSms">
              {{ smsCountdown > 0 ? smsCountdown + 's' : smsSending ? '发送中…' : '发验证码' }}
            </button>
          </div>
          <p v-if="loginError" class="login-error">{{ loginError }}</p>
          <button :disabled="!phoneInput.trim() || !codeInput.trim()" @click="submitPhoneLogin">进入指挥台</button>
        </template>
        <template v-else>
          <input
            v-model="loginInput"
            type="password"
            placeholder="访问令牌 Access Token"
            autofocus
            @keyup.enter="!$event.isComposing && submitToken()"
          />
          <p v-if="loginError" class="login-error">{{ loginError }}</p>
          <button :disabled="!loginInput.trim()" @click="submitToken">进入指挥台</button>
        </template>
      </div>
    </div>

    <header class="header">
      <div class="brand">
        <img class="brand-logo" src="/logo.svg" alt="语崽智脑" />
        <div class="brand-text">
          <span class="brand-title">语崽智脑</span>
          <span class="brand-sub">你的 AI 军团 · 一屏统管</span>
        </div>
      </div>
      <div class="view-tabs">
        <button :class="{ active: view === 'projects' }" @click="openProjects">项目</button>
        <template v-if="!isSaaS">
          <button :class="{ active: view === 'terminal' }" @click="openTerminalView">终端</button>
          <button :class="{ active: view === 'bridge' }" @click="openBridge">桌面会话</button>
          <button :class="{ active: view === 'plugins' }" @click="openPlugins">插件</button>
        </template>
      </div>
      <div class="agent-status">
        <select
          v-if="onlineComputers.length > 1"
          class="device-picker"
          :value="currentDeviceId"
          title="选择看哪台电脑"
          @change="pickComputer(($event.target as HTMLSelectElement).value)"
        >
          <option value="">主电脑（自动）</option>
          <option v-for="c in onlineComputers" :key="c.deviceId" :value="c.deviceId">💻 {{ deviceLabel(c.deviceId) }}</option>
        </select>
        <span class="status-capsule" :class="{ online: connected && authenticated && agentOnline, dead: monitorDead }">
          <span class="status-dot"></span>{{ monitorDead ? '监管断线' : agentOnline ? '监管中' : statusText }}
        </span>
        <button class="logout-btn" @click="logout">退出</button>
      </div>
    </header>

    <template v-if="view === 'projects'">
      <div class="projects-view">
        <div class="projects-toolbar">
          <span>项目（{{ projects.length }}）</span>
          <button class="logout-btn" @click="refreshProjects">刷新</button>
        </div>
        <div v-if="monitorDead" class="dead-banner">⚠️ 监管心跳断了——动画停住不是在偷懒，是"看着的人"掉线了，正在自动重连…</div>
        <div v-if="attentionItems.length" class="attention-banner">
          <div class="ab-head">
            <span class="ab-title" @click="attnCollapsed = !attnCollapsed" style="cursor: pointer">
              🔔 {{ attentionItems.length }} 个项目等你查看 {{ attnCollapsed ? '▸' : '▾' }}
            </span>
            <button class="ab-ack-all" @click="ackAll">全部已读</button>
          </div>
          <template v-if="!attnCollapsed">
            <div v-for="item in attentionItems.slice(0, 3)" :key="item.key" class="ab-item" @click="ackItem(item)">
              <span class="ab-reason" :class="'r-' + item.reason">
                {{ item.reason === 'error' ? '有问题' : item.reason === 'stalled' ? '疑似卡住' : item.reason === 'exit' ? '停止了' : '干完了' }}
              </span>
              <span class="ab-proj">{{ item.project }}</span>
              <span class="ab-line">{{ item.oneLine }}</span>
              <span class="ab-go">去看 ›</span>
            </div>
            <div v-if="attentionItems.length > 3" class="ab-more" @click="ackAll">
              还有 {{ attentionItems.length - 3 }} 条——点我全部已读（或逐个点上面的去看）
            </div>
          </template>
        </div>
        <div class="proj-strip">
          <div
            v-for="p in projects"
            :key="p.paneId"
            class="proj-card"
            :class="'st-' + p.state"
            @click="focusProject(p)"
          >
            <div class="proj-head">
              <span class="proj-state">{{ stateLabel(p) }}</span>
              <span v-if="attnOf(p)" class="attn-dot" title="这个项目等你查看">●</span>
              <span v-if="p.alert" class="proj-alert" title="末尾输出含报错字样">⚠️</span>
              <span
                v-if="liveOf(p) !== 'none'"
                class="live-lamp"
                :class="'live-' + liveOf(p)"
                :title="'活性：' + liveLabel(liveOf(p))"
              ></span>
              <button class="proj-close" title="关闭这个项目（存档不丢）" @click.stop="askCloseProject(p)">✕</button>
            </div>
            <div class="proj-name">{{ p.project }}</div>
            <div class="proj-meta">
              {{ p.session }}:{{ p.windowIndex }} · {{ p.command
              }}<template v-if="p.durationSec"> · 已跑 {{ fmtDuration(p.durationSec) }}</template>
            </div>
            <pre class="proj-tail">{{ p.tail.join('\n') }}</pre>
          </div>
          <div v-if="projectsLoading && !projects.length" class="empty">加载中…</div>
          <div v-else-if="!projects.length" class="empty">没有发现 tmux 窗口（在电脑上开几个项目窗口再来）</div>
        </div>
        <div v-if="projectsError" class="send-error">{{ projectsError }}</div>

        <div class="chat-panel">
          <div ref="chatPanelRef" class="chat-log" @scroll.passive="onChatScroll">
            <div v-if="!chatMessages.length" class="chat-hint">
              我是项目总监。问我"各项目怎么样了"、说"看看商城项目"、或让我"让它继续"都可以。
            </div>
            <div v-for="(m, i) in chatMessages" :key="i" class="cm" :class="'cm-' + m.kind">
              <img v-if="m.image" :src="m.image" class="cm-img" alt="发的图片" @click="imageViewer = m.image" />
              <div v-if="m.kind === 'card'" class="cm-cardbody">
                <div class="cm-cardline">
                  <span class="cm-cardicon">{{ m.cardKind === 'success' ? '✅' : m.cardKind === 'error' ? '⚠️' : 'ℹ️' }}</span>
                  <b>{{ m.project }}</b> · {{ m.text }}
                  <button class="tts-btn" title="朗读这条" @click="speakOnce(m.project + '，' + m.text)">🔊</button>
                </div>
                <div v-if="m.detail" class="cm-carddetail">{{ m.detail }}</div>
                <div v-if="m.suggestion" class="cm-cardsugg">建议：{{ m.suggestion }}</div>
              </div>
              <template v-else>
                <div v-if="m.text" class="cm-text">{{ m.text }}</div>
                <button v-if="m.kind === 'text'" class="tts-btn" title="朗读这条" @click="speakOnce(m.text)">🔊</button>
              </template>
              <div v-if="m.kind === 'confirm_req' && !m.answered" class="cm-actions">
                <button class="cm-ok" @click="answerConfirm(m.confirmId!, true)">确认执行</button>
                <button class="cm-no" @click="answerConfirm(m.confirmId!, false)">取消</button>
              </div>
            </div>
            <div v-if="chatBusy" class="cm cm-text2"><div class="cm-text">总监在想…</div></div>
          </div>
          <button v-if="chatNewCount" class="new-msg-pill" @click="scrollChatPanel(true)">↓ {{ chatNewCount }} 条新消息</button>
          <div v-if="pendingImagePreview" class="img-preview">
            <img :src="pendingImagePreview" alt="待发送" />
            <button class="img-preview-x" title="移除图片" @click="clearPendingImage">✕</button>
          </div>
          <div v-if="voiceMode" class="voice-bar">
            <span class="vb-dot" :class="'vs-' + voiceState"></span>
            <span class="vb-text">{{ voiceStateText }}</span>
            <button class="vb-exit" title="退出对讲" @click="exitVoice">✕ 退出对讲</button>
          </div>
          <div class="chat-input-row">
            <input ref="imageFileInput" type="file" accept="image/jpeg,image/png,image/webp" style="display: none" @change="onImagePicked" />
            <div class="plus-wrap">
              <button class="plus-btn" :class="{ open: chatMenuOpen }" title="更多功能" @click="chatMenuOpen = !chatMenuOpen">＋</button>
              <div v-if="chatMenuOpen" class="plus-backdrop" @click="chatMenuOpen = false"></div>
              <div v-if="chatMenuOpen" class="plus-menu">
                <button @click="pickImage(); chatMenuOpen = false">📷 发图片<span class="pm-hint">报错截图/照片，总监看得懂</span></button>
                <button @click="toggleTts()">{{ ttsOn ? '🔊 朗读：开' : '🔇 朗读：关' }}<span class="pm-hint">点我{{ ttsOn ? '关' : '开' }}，开了自动念给你听</span></button>
                <button :disabled="chatBusy" @click="askNextSteps(); chatMenuOpen = false">💡 下一步建议<span class="pm-hint">总监按当前项目状态出主意</span></button>
                <button @click="openFolderPicker(); chatMenuOpen = false">📂 打开项目<span class="pm-hint">点选文件夹开新项目，不用说名字</span></button>
              </div>
            </div>
            <button class="mic-btn" :class="'vs-' + voiceState" :title="voiceState === 'listening' ? '点我收口（说完也可以直接点）' : '点我说话（语音对讲）'" @click="tapMic">🎙</button>
            <textarea
              ref="chatInputEl"
              v-model="chatInput"
              rows="1"
              :placeholder="voiceState === 'listening' ? '正在听…' : '跟总监说话…'"
              @input="autoGrowChatInput"
              @keydown.enter.exact.prevent="!$event.isComposing && sendChat()"
            ></textarea>
            <button
              v-if="chatInput.includes('\n') || chatInput.length > 60 || inputExpanded"
              class="expand-btn"
              :title="inputExpanded ? '收回小条' : '放大编辑（长文好用）'"
              @click="inputExpanded = !inputExpanded; nextTick(autoGrowChatInput)"
            >{{ inputExpanded ? '↙' : '⤢' }}</button>
            <button class="send-btn" :disabled="chatBusy || (!chatInput.trim() && !pendingImage)" @click="sendChat">发送</button>
          </div>
        </div>
      </div>
    </template>

    <div v-if="imageViewer" class="img-viewer" @click="imageViewer = ''">
      <img :src="imageViewer" alt="查看图片" />
    </div>

    <!-- 点选开项目抽屉（五期·十九）：真实文件夹清单，点哪个开哪个 -->
    <div v-if="folderPicker" class="sheet-backdrop" @click="folderPicker = null"></div>
    <div v-if="folderPicker" class="folder-sheet">
      <div class="fs-head">
        <span class="fs-title">打开项目——点哪个开哪个</span>
        <button class="fs-x" title="关上" @click="folderPicker = null">✕</button>
      </div>
      <div class="fs-hint">你电脑里的项目文件夹都在这了（用户目录在最前，往下是桌面/文稿/下载），真实列出不用猜名字</div>
      <div v-if="folderPicker.loading" class="empty">正在问电脑要文件夹清单…</div>
      <template v-else>
        <div v-if="folderPicker.failedRoots.length" class="fs-warn">有几个位置没读出来：{{ folderPicker.failedRoots.join('、') }}（权限不够或不存在）</div>
        <div v-if="!folderPicker.folders.length" class="empty">常用位置一个文件夹都没有</div>
        <div class="fs-list">
          <button v-for="f in folderPickerSorted" :key="f.path" class="fs-item" @click="folderConfirm = f">
            <span class="fs-name">📁 {{ f.label }}</span>
            <span class="fs-path">{{ f.where }} · {{ fmtFolderPath(f.path) }}</span>
          </button>
        </div>
      </template>
      <div v-if="folderOpening" class="fs-opening">正在打开「{{ fmtFolderPath(folderOpening) }}」…</div>
    </div>

    <!-- 点选后的本地二次确认 -->
    <div v-if="folderConfirm" class="sheet-backdrop" @click="folderConfirm = null"></div>
    <div v-if="folderConfirm" class="confirm-sheet">
      <div class="cs-title">打开「{{ folderConfirm.label }}」？</div>
      <div class="cs-path">路径 {{ fmtFolderPath(folderConfirm.path) }}，AI 会在这个文件夹里开工</div>
      <div class="cs-actions">
        <button class="cm-ok" @click="confirmOpenFolder">确认打开</button>
        <button class="cm-no" @click="folderConfirm = null">取消</button>
      </div>
    </div>

    <!-- 关项目确认（卡片 ✕ 点开后） -->
    <div v-if="closeConfirm" class="sheet-backdrop" @click="closeConfirm = null"></div>
    <div v-if="closeConfirm" class="confirm-sheet">
      <div class="cs-title">关闭「{{ closeConfirm.project }}」？</div>
      <div class="cs-path">
        干活的会话会停下，存档不丢，以后说"打开 {{ closeConfirm.project }}"能接着开。
        <template v-if="(closeConfirm.attached || 0) > 0">⚠️ 这台电脑上正有人连着看这个窗口，关了那边就断了。</template>
      </div>
      <div class="cs-actions">
        <button class="cm-ok" style="background: #b3553f; color: #fff" @click="doCloseProject">确认关闭</button>
        <button class="cm-no" @click="closeConfirm = null">先不关</button>
      </div>
    </div>

    <template v-if="view === 'plugins'">
      <div class="plugins-view">
        <div class="projects-toolbar">
          <span>插件中心（{{ pluginsList.length }}）</span>
          <button class="logout-btn" @click="refreshPlugins">刷新</button>
        </div>
        <div class="bridge-hint">插件是接进来的能力——你的项目有 HTTP 接口或 MCP 服务，放张登记表就能被老板用大白话调用。停用的插件总监不再调用。</div>
        <div class="bridge-scroll">
          <div v-for="p in pluginsList" :key="p.name" class="plugin-item">
            <div class="plugin-main">
              <div class="plugin-title">
                {{ p.title }}
                <span class="plugin-risk" :class="'rk-' + p.risk">{{ p.risk }}</span>
                <span class="plugin-exec">{{ p.executor === 'mcp' ? 'MCP' : 'HTTP' }}</span>
                <span v-if="!p.enabled" class="plugin-off">已停用</span>
              </div>
              <div class="plugin-desc">{{ p.description }}</div>
              <div v-if="pluginTestResult[p.name]" class="plugin-test" :class="{ bad: !pluginTestResult[p.name].ok }">
                {{ pluginTestResult[p.name].ok ? '✅' : '❌' }} {{ pluginTestResult[p.name].detail }}
              </div>
            </div>
            <div class="plugin-actions">
              <button v-if="p.hasSelftest" class="mini-btn" @click="runPluginSelftest(p.name)">自检</button>
              <button class="mini-btn" :class="{ warn: p.enabled }" @click="togglePlugin(p)">{{ p.enabled ? '停用' : '启用' }}</button>
            </div>
          </div>
          <div v-if="pluginsLoading && !pluginsList.length" class="empty">加载中…</div>
          <div v-else-if="!pluginsList.length" class="empty">还没有插件。把登记表 .json 放进 ~/.yuzai-plugins/ 就有第一个了</div>
        </div>
      </div>
    </template>

    <div v-show="view === 'terminal'" class="terminal-view">
      <div ref="terminalRef" class="terminal"></div>

      <div class="fn-bar">
        <button @pointerdown.prevent @click="sendFnKey('\x1b')">Esc</button>
        <button :class="{ armed: ctrlArmed }" @pointerdown.prevent @click="toggleCtrl">Ctrl</button>
        <button @pointerdown.prevent @click="sendFnKey('\t')">Tab</button>
        <button @pointerdown.prevent @click="sendFnKey('\x02')">⌃B</button>
        <button @pointerdown.prevent @click="sendFnKey('\x03')">⌃C</button>
        <button @pointerdown.prevent @click="sendFnKey('\x04')">⌃D</button>
        <button @pointerdown.prevent @click="sendFnKey('\x1b[D')">←</button>
        <button @pointerdown.prevent @click="sendFnKey('\x1b[A')">↑</button>
        <button @pointerdown.prevent @click="sendFnKey('\x1b[B')">↓</button>
        <button @pointerdown.prevent @click="sendFnKey('\x1b[C')">→</button>
        <button class="enter-btn" title="回车" @pointerdown.prevent @click="sendFnKey('\r')">↵</button>
        <button @pointerdown.prevent @click="openPty(true)">⟳</button>
        <span class="fn-sep"></span>
        <button title="tmux 会话树（任何界面有效）" @pointerdown.prevent @click="sendFnKey('\x02s')">会话</button>
        <button title="tmux 窗口树（任何界面有效）" @pointerdown.prevent @click="sendFnKey('\x02w')">窗口</button>
        <button title="tmux 新建窗口" @pointerdown.prevent @click="sendFnKey('\x02c')">新窗</button>
        <button title="tmux 滚屏回看（q 退出）" @pointerdown.prevent @click="sendFnKey('\x02[')">滚屏</button>
        <button title="列出所有 tmux 会话（shell 下用）" @pointerdown.prevent @click="sendFnKey('tmux ls\r')">tmux ls</button>
        <button title="列出当前目录（shell 下用）" @pointerdown.prevent @click="sendFnKey('ls\r')">ls</button>
        <button title="返回上级目录（shell 下用）" @pointerdown.prevent @click="sendFnKey('cd ..\r')">cd..</button>
        <button title="显示当前路径（shell 下用）" @pointerdown.prevent @click="sendFnKey('pwd\r')">pwd</button>
        <button title="清屏（shell 下用）" @pointerdown.prevent @click="sendFnKey('clear\r')">clear</button>
      </div>
    </div>

    <template v-if="view === 'bridge'">
      <div v-if="bridgeMode === 'list'" class="bridge-list">
        <div class="bridge-toolbar">
          <span>桌面会话（{{ bridgeSessions.length }}）</span>
          <button class="logout-btn" @click="refreshSessions">刷新</button>
        </div>
        <div class="bridge-hint">这里按项目分组，摆的是你电脑上 AI 干活的历史会话存档——点进去能回看它干了什么。</div>
        <div class="bridge-scroll">
          <div v-for="g in groupedSessions" :key="g.project" class="project-group">
            <div class="project-name">📁 {{ g.project }}<span class="pg-count">{{ g.sessions.length }}{{ g.more ? '+' + g.more : '' }} 个会话</span></div>
            <div
              v-for="s in g.sessions"
              :key="s.sessionId"
              class="session-item"
              @click="openSession(s)"
            >
              <div class="session-title">{{ s.title }}</div>
              <div class="session-meta">
                {{ formatTime(s.updatedAt) }}<span v-if="s.lastPrompt"> · {{ s.lastPrompt }}</span>
              </div>
            </div>
            <div v-if="g.more" class="group-more">… 还有 {{ g.more }} 条更早的会话（按时间倒序，此处只摆最近 20 条）</div>
          </div>
          <div v-if="!bridgeSessions.length" class="empty">加载中…</div>
        </div>
      </div>

      <div v-else class="bridge-chat">
        <div class="chat-header">
          <button class="back-btn" @click="backToList">‹ 返回</button>
          <span class="chat-title">{{ activeSession?.title }}</span>
        </div>
        <div ref="chatRef" class="chat-messages" @scroll.passive="onBridgeScroll">
          <div v-for="(ev, i) in bridgeEvents" :key="i">
            <div v-if="ev.kind === 'status'" class="tool-line">
              <span class="tool-chip">{{ ev.text }}</span>
            </div>
            <div v-else-if="ev.kind === 'turn_end'" class="turn-divider"></div>
            <div v-else class="msg" :class="ev.role === 'user' || ev.kind === 'user' ? 'msg-user' : ev.role === 'think' ? 'msg-think' : 'msg-assistant'">
              <div class="msg-role">{{ roleLabel(ev) }}</div>
              <div v-if="ev.role === 'assistant'" class="msg-text md" v-html="renderMarkdown(ev.text)"></div>
              <div v-else class="msg-text">{{ ev.text }}</div>
            </div>
          </div>
          <div v-if="!bridgeEvents.length" class="empty">加载中…</div>
        </div>
        <button v-if="bridgeNewCount" class="new-msg-pill" @click="scrollChatToBottom(true)">↓ {{ bridgeNewCount }} 条新消息</button>
        <div class="chat-input">
          <input
            v-model="bridgeInput"
            type="text"
            placeholder="发消息到此会话…"
            @keyup.enter="!$event.isComposing && sendBridgeMessage()"
          />
          <button @click="sendBridgeMessage">发送</button>
        </div>
        <div v-if="bridgeSendError" class="send-error">{{ bridgeSendError }}</div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.container {
  /* ---- 语崽智脑设计令牌 ---- */
  --bg: #0d1017;
  --bg2: #12162a;
  --surface: rgba(26, 31, 49, 0.72);
  --surface-solid: #1a1f31;
  --border: rgba(255, 255, 255, 0.09);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #e6eaf5;
  --dim: #8a93ad;
  --accent: #6366f1;
  --accent2: #22d3ee;
  --ok: #34d399;
  --warn: #fbbf24;
  --err: #fb7185;

  display: flex;
  flex-direction: column;
  /* 钉死为 app-shell：文档永远不可滚动，杜绝"header 被滚出视口"的机型玄学 */
  position: fixed;
  inset: 0;
  height: 100vh;
  height: 100dvh;
  height: var(--vvh, 100dvh);
  overflow: hidden;
  /* APP 壳内由原生注入 --top-lift（状态栏高，WebView padding 实测无效后的真修法）；浏览器默认 0 */
  padding-top: var(--top-lift, 0px);
  color: var(--text);
  background:
    radial-gradient(110% 70% at 85% -10%, rgba(99, 102, 241, 0.22), transparent 55%),
    radial-gradient(90% 60% at 5% 110%, rgba(34, 211, 238, 0.14), transparent 50%),
    linear-gradient(180deg, var(--bg2), var(--bg) 40%);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  padding: 10px 14px;
  /* 不用 backdrop-filter：部分安卓 WebView 会把它渲染成全透明，整个 header 隐形（已踩坑） */
  background: rgba(13, 16, 23, 0.92);
  border-bottom: 1px solid var(--border);
  font-size: 14px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.brand-logo {
  width: 34px;
  height: 34px;
  filter: drop-shadow(0 3px 8px rgba(34, 211, 238, 0.35));
}

.brand-text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.brand-title {
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 1.5px;
  background: linear-gradient(100deg, #a5b4fc, #22d3ee 65%, #34d399);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  white-space: nowrap;
}

.brand-sub {
  font-size: 10px;
  color: var(--dim);
  letter-spacing: 1px;
  white-space: nowrap;
}

.agent-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-capsule {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--dim);
  font-size: 12px;
  white-space: nowrap;
}

.status-capsule .status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--warn);
}

.status-capsule.online {
  color: var(--ok);
  border-color: rgba(52, 211, 153, 0.4);
}

.status-capsule.online .status-dot {
  background: var(--ok);
  box-shadow: 0 0 8px var(--ok);
  animation: pulse-dot 2s ease-in-out infinite;
}

.status-capsule.dead {
  color: var(--err);
  border-color: rgba(251, 113, 133, 0.5);
}

.status-capsule.dead .status-dot {
  background: var(--err);
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(0.8); }
}

.terminal {
  flex: 1;
  padding: 8px;
  overflow: hidden;
}

.terminal-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.fn-bar {
  display: flex;
  gap: 6px;
  padding: 8px 10px;
  /* 实测 vivo 三键导航栏 ~60 CSS px；env() 在其 WebView 回报 0。
     先写死值兜底（老 WebView 丢 calc 声明时也有效），calc+env 增强 */
  padding-bottom: var(--nav-lift, 72px);
  padding-bottom: calc(var(--nav-lift, 72px) + env(safe-area-inset-bottom, 0px));
  background: #252526;
  border-top: 1px solid #3c3c3c;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  user-select: none;
  -webkit-user-select: none;
}

.fn-sep {
  flex: 0 0 auto;
  align-self: stretch;
  width: 1px;
  background: #555;
  margin: 2px 4px;
}

.fn-bar button {
  flex: 0 0 auto;
  min-width: 44px;
  padding: 10px 12px;
  background: #3c3c3c;
  border: 1px solid #555;
  border-radius: 6px;
  color: #d4d4d4;
  font-size: 14px;
  font-family: Menlo, monospace;
  cursor: pointer;
  touch-action: manipulation;
}

.fn-bar button:active {
  background: #007acc;
  border-color: #007acc;
}

.fn-bar button.armed {
  background: #007acc;
  border-color: #007acc;
  color: #fff;
}

.login-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(120% 90% at 80% -10%, rgba(99, 102, 241, 0.35), transparent 55%),
    radial-gradient(100% 80% at 10% 110%, rgba(34, 211, 238, 0.22), transparent 50%),
    var(--bg);
}

.login-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  width: min(340px, 86vw);
  padding: 36px 30px;
  background: rgba(26, 31, 49, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.login-logo {
  width: 84px;
  height: 84px;
  filter: drop-shadow(0 8px 20px rgba(34, 211, 238, 0.35));
}

.brand-name {
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: 2px;
  background: linear-gradient(100deg, #a5b4fc, #22d3ee 60%, #34d399);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.brand-tagline {
  margin: -6px 0 8px;
  color: var(--dim);
  font-size: 13px;
  letter-spacing: 3px;
}

.login-hero input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  color: var(--text);
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

.login-hero input:focus {
  border-color: var(--accent2);
  box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.18);
}

.login-hero .login-error {
  margin: 0;
  color: var(--err);
  font-size: 13px;
}

.login-tabs {
  display: flex;
  gap: 8px;
  width: 100%;
}

.login-tabs button {
  flex: 1;
  background: #2c2c2e !important;
  color: #888 !important;
}

.login-tabs button.cur {
  background: linear-gradient(100deg, #6366f1, #22d3ee) !important;
  color: #fff !important;
}

.login-code-row {
  display: flex;
  gap: 8px;
  width: 100%;
}

.login-code-row input {
  flex: 1;
  min-width: 0;
}

.login-code-row .sms-btn {
  flex: 0 0 auto;
  width: auto; /* 盖掉 .login-hero button 的 width:100%，否则验证码输入框被挤没 */
  padding: 0 14px;
  white-space: nowrap;
}

.login-hero button {
  width: 100%;
  padding: 12px;
  background: linear-gradient(100deg, var(--accent), var(--accent2));
  border: none;
  border-radius: 10px;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(99, 102, 241, 0.35);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.login-hero button:active {
  transform: translateY(1px);
}

.login-hero button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.logout-btn {
  margin-left: 8px;
  padding: 3px 10px;
  background: transparent;
  border: 1px solid #555;
  border-radius: 4px;
  color: #888;
  font-size: 12px;
  cursor: pointer;
}

.logout-btn:hover {
  color: #d4d4d4;
  border-color: #888;
}

.view-tabs {
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border);
}

.view-tabs button {
  padding: 5px 16px;
  background: transparent;
  border: none;
  border-radius: 999px;
  color: var(--dim);
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.view-tabs button.active {
  background: linear-gradient(100deg, var(--accent), var(--accent2));
  color: #fff;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

/* ---- 项目总览 ---- */
.projects-view {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.projects-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  color: #8a93ad;
  font-size: 13px;
}

.proj-strip {
  flex: 0 0 auto;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  -webkit-overflow-scrolling: touch;
}

.proj-strip .proj-card {
  flex: 0 0 76%;
  max-width: 340px;
  scroll-snap-align: start;
}

.proj-strip .empty {
  padding: 18px 8px;
  color: #8a93ad;
  font-size: 13px;
}

.attn-dot {
  color: #fb7185;
  font-size: 11px;
  animation: mic-pulse 1.2s ease-in-out infinite;
}

/* 汇报卡片气泡（对话流里） */
.cm-cardbody {
  max-width: 92%;
  background: rgba(95, 126, 207, 0.1);
  border: 1px solid rgba(95, 126, 207, 0.35);
  border-radius: 12px;
  padding: 9px 12px;
  font-size: 14px;
  line-height: 1.55;
}

.cm-cardline {
  color: #d7dce8;
}

.cm-cardicon {
  margin-right: 4px;
}

.cm-carddetail {
  margin-top: 5px;
  color: #9aa3b5;
  font-size: 13px;
}

.cm-cardsugg {
  margin-top: 5px;
  color: #7ecfa0;
  font-size: 13px;
}

.proj-card {
  position: relative;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 12px 14px;
  cursor: pointer;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  transition: transform 0.12s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.proj-card:hover {
  transform: translateY(-2px);
  border-color: var(--border-strong);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.proj-card:active {
  transform: translateY(0);
}

.proj-card.st-ai {
  border-left: 3px solid var(--accent2);
}

.proj-card.st-running {
  border-left: 3px solid var(--warn);
}

.proj-card.st-idle {
  border-left: 3px solid rgba(255, 255, 255, 0.15);
}

.proj-head {
  display: flex;
  align-items: center;
  gap: 6px;
}

.proj-state {
  font-size: 12px;
  color: var(--ok);
  font-family: Menlo, monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.st-running .proj-state {
  color: var(--warn);
}

.st-idle .proj-state {
  color: var(--dim);
}

.proj-alert {
  flex: 0 0 auto;
}

/* 活性心跳灯——由哨兵实测驱动，不是装饰 */
.live-lamp {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--dim);
}

.live-lamp.live-active {
  background: var(--ok);
  box-shadow: 0 0 8px var(--ok);
  animation: live-spin 1.1s ease-in-out infinite;
}

.live-lamp.live-quiet {
  background: var(--warn);
  animation: live-spin 2.8s ease-in-out infinite;
  opacity: 0.85;
}

.live-lamp.live-stalled {
  background: var(--err);
  /* 卡住 = 不动（不演） */
}

@keyframes live-spin {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.72); }
}

/* 监管断线：全屏动画集体定格（看着的人倒了，不装活） */
.monitor-dead .live-lamp,
.monitor-dead .status-dot {
  animation: none !important;
}

/* 待查看闹铃横幅 */
.dead-banner {
  padding: 9px 14px;
  background: rgba(251, 113, 133, 0.12);
  border-bottom: 1px solid rgba(251, 113, 133, 0.35);
  color: var(--err);
  font-size: 12px;
}

.attention-banner {
  flex: 0 0 auto;
  border-bottom: 1px solid rgba(251, 191, 36, 0.3);
  background: linear-gradient(100deg, rgba(251, 191, 36, 0.1), rgba(251, 113, 133, 0.08));
  padding: 8px 12px;
  /* 展开最多摆 3 条+一行"还有 N 条"（计数截断），不做高度裁切——切半行像叠影（已踩坑） */
}

.ab-more {
  padding: 7px 4px 2px;
  font-size: 12px;
  color: #e5c07b;
  cursor: pointer;
}

.ab-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.ab-title {
  color: var(--warn);
  font-size: 13px;
  font-weight: 600;
}

.ab-ack-all {
  padding: 3px 12px;
  background: transparent;
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  color: var(--dim);
  font-size: 12px;
  cursor: pointer;
}

.ab-ack-all:hover {
  color: var(--text);
}

.ab-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  margin: 4px 0;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid var(--border);
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
}

.ab-item:active {
  background: rgba(0, 0, 0, 0.4);
}

.ab-reason {
  flex: 0 0 auto;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
}

.ab-reason.r-done { background: rgba(52, 211, 153, 0.18); color: var(--ok); }
.ab-reason.r-exit { background: rgba(138, 147, 173, 0.2); color: var(--dim); }
.ab-reason.r-error { background: rgba(251, 113, 133, 0.18); color: var(--err); }
.ab-reason.r-stalled { background: rgba(251, 191, 36, 0.18); color: var(--warn); }

.ab-proj {
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
}

.ab-line {
  flex: 1;
  color: var(--dim);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ab-go {
  flex: 0 0 auto;
  color: var(--accent2);
  font-size: 12px;
}

.proj-name {
  color: #d4d4d4;
  font-size: 16px;
  font-weight: 600;
  margin: 4px 0 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.proj-meta {
  color: #777;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.proj-tail {
  margin-top: 8px;
  padding: 6px 8px;
  background: #1a1a1a;
  border-radius: 4px;
  color: #888;
  font-family: Menlo, monospace;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 11px;
  line-height: 1.4;
  max-height: 96px;
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-all;
}

/* ---- 对话式总管 ---- */
.chat-panel {
  flex: 1 1 auto; /* 两区制：对话区是主区，占满项目条以下的全部空间 */
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative; /* 新消息浮钮锚点 */
  border-top: 1px solid #3c3c3c;
  background: #222223;
  /* 底部抬升用 margin 不用 padding：padding 会让输入行自身高过 38vh 被裁掉（键盘弹起输入行消失案的真相） */
  margin-bottom: var(--nav-lift, 72px);
  margin-bottom: calc(var(--nav-lift, 72px) + env(safe-area-inset-bottom, 0px));
}

.chat-log {
  flex: 1;
  overflow-y: auto;
  padding: 8px 10px;
}

.chat-hint {
  color: #777;
  font-size: 12px;
  padding: 8px 4px;
  line-height: 1.6;
}

.cm {
  margin: 6px 0;
  max-width: 92%;
}

.cm-text {
  padding: 7px 11px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.cm-user {
  margin-left: auto;
}

.cm-user .cm-text {
  background: #0e5a8a;
  color: #fff;
}

.cm-text2 .cm-text,
.cm-text .cm-text {
  background: transparent;
}

.cm-text > .cm-text {
  padding: 0;
}

.cm-text,
.cm-tool .cm-text {
  background: #252526;
  border: 1px solid #3c3c3c;
  color: #d4d4d4;
}

.cm-tool .cm-text {
  color: #b9b9b9;
  font-size: 13px;
}

.cm-confirm_req .cm-text {
  background: #2d2a20;
  border: 1px solid #6b5d2a;
  color: #e8d98a;
}

.cm-actions {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

.cm-actions button {
  padding: 7px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.cm-ok {
  background: #23d18b;
  color: #06281a;
}

.tts-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  opacity: 0.55;
  padding: 0 4px;
  vertical-align: middle;
}

.tts-btn:hover {
  opacity: 1;
}

.tts-toggle {
  flex: 0 0 auto;
  min-width: 44px;
  background: #3c3c3c;
  border: 1px solid #555;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
}

.tts-toggle.on {
  background: linear-gradient(100deg, #6366f1, #22d3ee);
  border-color: transparent;
}

.next-btn {
  flex: 0 0 auto;
  min-width: 44px;
  background: #3c3c3c;
  border: 1px solid #555;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
}

.next-btn:disabled {
  opacity: 0.4;
}

.cm-img {
  max-width: 180px;
  max-height: 180px;
  border-radius: 8px;
  cursor: zoom-in;
  display: block;
  margin-bottom: 4px;
}

.img-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #252526;
  border-top: 1px solid #3c3c3c;
}

.img-preview img {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
}

.img-preview-x {
  background: #3c3c3c;
  border: none;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  color: #ccc;
  cursor: pointer;
}

.img-viewer {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99;
  cursor: zoom-out;
}

.img-viewer img {
  max-width: 96vw;
  max-height: 92vh;
  border-radius: 8px;
}

/* ---- 点选开/关项目（五期·十九） ---- */
.proj-close {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  color: #999;
  font-size: 13px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.proj-close:active {
  background: #5a2a1a;
  color: #fff;
}
.sheet-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 60;
}
.folder-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 61;
  background: var(--surface, #1e1e20);
  border-top: 1px solid var(--border, #333);
  border-radius: 14px 14px 0 0;
  max-height: 62vh;
  display: flex;
  flex-direction: column;
  padding: 14px 14px calc(14px + env(safe-area-inset-bottom));
}
.fs-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.fs-title {
  font-weight: 700;
  color: var(--text, #eee);
}
.fs-x {
  background: none;
  color: #888;
  padding: 4px 8px;
}
.fs-hint {
  color: #888;
  font-size: 12px;
  margin: 6px 0 10px;
}
.fs-warn {
  color: #fbbf24;
  font-size: 12px;
  margin-bottom: 8px;
}
.fs-list {
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.fs-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  background: #2c2c2e;
  border: 1px solid #3a3a3c;
  border-radius: 10px;
  padding: 10px 12px;
  color: var(--text, #eee);
  text-align: left;
}
.fs-item:active {
  border-color: #6366f1;
}
.fs-name {
  font-size: 16px;
  font-weight: 600;
}
.fs-path {
  font-size: 12px;
  color: #888;
  word-break: break-all;
}
.fs-opening {
  color: #4ec9b0;
  font-size: 13px;
  margin-top: 8px;
}
.confirm-sheet {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 24vh;
  z-index: 62;
  background: var(--surface, #1e1e20);
  border: 1px solid var(--border, #333);
  border-radius: 12px;
  padding: 16px;
  max-width: 480px;
  margin: 0 auto;
}
.cs-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--text, #eee);
}
.cs-path {
  color: #aaa;
  font-size: 13px;
  margin: 8px 0 14px;
  word-break: break-all;
}
.cs-actions {
  display: flex;
  gap: 10px;
}
.cs-actions button {
  flex: 1;
}

.device-picker {
  background: #2c2c2e;
  color: #d4d4d4;
  border: 1px solid #444;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 13px;
  max-width: 150px;
}

.cm-no {
  background: #3c3c3c;
  color: #d4d4d4;
  border: 1px solid #555 !important;
}

.cm-confirm_done .cm-text {
  color: #999;
  font-size: 12px;
  background: transparent;
  border: none;
  padding: 2px 11px;
}

.cm-error .cm-text {
  background: #332020;
  border: 1px solid #6b3232;
  color: #f1a0a0;
}

.chat-input-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 10px;
  background: #252526;
  border-top: 1px solid #3c3c3c;
}

.chat-input-row textarea {
  flex: 1;
  min-width: 0; /* 收缩许可：不带它输入框有固有最小宽，加按钮后把「发送」挤出屏 */
  padding: 10px 12px;
  background: #3c3c3c;
  border: 1px solid #555;
  border-radius: 10px;
  color: #d4d4d4;
  font-size: 15px;
  line-height: 1.45;
  font-family: inherit;
  outline: none;
  resize: none;
  overflow-y: hidden;
}

.chat-input-row textarea:focus {
  border-color: #007acc;
}

.plus-wrap {
  position: relative;
  flex: 0 0 auto;
}

.plus-btn {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #3c3c3c;
  border: 1px solid #555;
  color: #d4d4d4;
  font-size: 20px;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.plus-btn.open {
  transform: rotate(45deg);
  background: linear-gradient(100deg, #6366f1, #22d3ee);
  border-color: transparent;
  color: #fff;
}

.plus-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}

.plus-menu {
  position: absolute;
  left: 0;
  bottom: 52px;
  z-index: 41;
  width: 240px;
  background: #2c2c2e;
  border: 1px solid #4a4a4d;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
}

.plus-menu button {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: #e6e6e6;
  font-size: 15px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
}

.plus-menu button:active {
  background: #3a3a3d;
}

.plus-menu button:disabled {
  opacity: 0.4;
}

.plus-menu .pm-hint {
  display: block;
  font-size: 12px;
  color: #8a8a8e;
  margin-top: 2px;
}

.chat-input-row .send-btn {
  flex: 0 0 auto;
  padding: 10px 18px;
  background: #007acc;
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 15px;
  cursor: pointer;
}

.expand-btn {
  flex: 0 0 auto;
  width: 34px;
  height: 42px;
  border-radius: 9px;
  border: 1px solid #3c3c3f;
  background: #2a2a2e;
  color: #8a8a8e;
  font-size: 16px;
  cursor: pointer;
}

.chat-input-row button:disabled {
  opacity: 0.5;
}

/* ---- 语音对讲 ---- */
.mic-btn {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid #3c3c3f;
  background: #2a2a2e;
  color: #e8e8ea;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mic-btn.vs-listening {
  background: #c42b1c;
  border-color: #c42b1c;
  animation: mic-pulse 1.2s ease-in-out infinite;
}

.mic-btn.vs-thinking,
.mic-btn.vs-speaking {
  background: #007acc;
  border-color: #007acc;
}

@keyframes mic-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(196, 43, 28, 0.55);
  }
  50% {
    box-shadow: 0 0 0 9px rgba(196, 43, 28, 0);
  }
}

.voice-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  margin: 0 10px 6px;
  background: #232326;
  border: 1px solid #3c3c3f;
  border-radius: 9px;
  font-size: 13px;
  color: #b8b8bc;
}

.vb-dot {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #5a5a5e;
}

.vb-dot.vs-listening {
  background: #e05260;
  animation: mic-pulse 1.2s ease-in-out infinite;
}

.vb-dot.vs-thinking {
  background: #e5c07b;
}

.vb-dot.vs-speaking {
  background: #61afef;
}

.vb-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vb-exit {
  flex: 0 0 auto;
  background: none;
  border: none;
  color: #8a8a8e;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 4px;
}

/* ---- 状态桥 ---- */
.bridge-list,
.bridge-chat {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative; /* 新消息浮钮锚点 */
}

.new-msg-pill {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 88px; /* 输入行上方 */
  z-index: 6;
  border: none;
  border-radius: 999px;
  padding: 7px 14px;
  background: var(--accent, #6366f1);
  color: #fff;
  font-size: 13px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}

.bridge-hint {
  color: #777;
  font-size: 12px;
  padding: 6px 14px;
  line-height: 1.6;
}

.plugins-view {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.plugin-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #2a2a2c;
}

.plugin-main {
  min-width: 0;
  flex: 1;
}

.plugin-title {
  font-weight: 600;
  color: #eee;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.plugin-risk {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #1a3a5a;
  color: #7cc7ff;
}

.plugin-risk.rk-L2,
.plugin-risk.rk-L3 {
  background: #5a4a1a;
  color: #ffd479;
}

.plugin-exec {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #2c2c2e;
  color: #999;
}

.plugin-off {
  font-size: 11px;
  color: #f48771;
}

.plugin-desc {
  color: #8a8a8e;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.5;
}

.plugin-test {
  font-size: 12px;
  margin-top: 6px;
  color: #4ec9b0;
}

.plugin-test.bad {
  color: #f48771;
}

.plugin-actions {
  display: flex;
  gap: 8px;
  flex: 0 0 auto;
}

.mini-btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #444;
  background: #2c2c2e;
  color: #d4d4d4;
  font-size: 13px;
  cursor: pointer;
}

.mini-btn.warn {
  background: #5a4a1a;
  border-color: #6b5a24;
}

.pg-count {
  margin-left: 8px;
  color: #777;
  font-size: 12px;
  font-weight: normal;
}

.group-more {
  color: #666;
  font-size: 12px;
  padding: 8px 16px 14px;
}

.enter-btn {
  font-weight: 700;
}

.bridge-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #3c3c3c;
  color: #888;
  font-size: 13px;
}

.bridge-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.project-name {
  padding: 10px 8px 4px;
  color: #888;
  font-size: 12px;
}

.session-item {
  padding: 10px 12px;
  margin: 4px 0;
  background: #252526;
  border: 1px solid #3c3c3c;
  border-radius: 6px;
  cursor: pointer;
}

.session-item:active {
  background: #2d2d30;
}

.session-title {
  color: #d4d4d4;
  font-size: 14px;
  margin-bottom: 3px;
}

.session-meta {
  color: #777;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #252526;
  border-bottom: 1px solid #3c3c3c;
}

.back-btn {
  padding: 4px 10px;
  background: transparent;
  border: 1px solid #555;
  border-radius: 4px;
  color: #d4d4d4;
  font-size: 13px;
  cursor: pointer;
}

.chat-title {
  color: #d4d4d4;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.msg {
  margin-bottom: 12px;
  max-width: 92%;
}

.msg-role {
  font-size: 11px;
  color: #777;
  margin-bottom: 2px;
}

.msg-text {
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.msg-user {
  margin-left: auto;
}

.msg-user .msg-text {
  background: #0e5a8a;
  color: #fff;
}

.msg-assistant .msg-text {
  background: #252526;
  border: 1px solid #3c3c3c;
  color: #d4d4d4;
}

/* markdown 富渲染（bridge 助手消息） */
.md :deep(h2), .md :deep(h3), .md :deep(h4), .md :deep(h5) {
  margin: 10px 0 6px;
  font-size: 15px;
  color: #e6eaf5;
}
.md :deep(p) { margin: 6px 0; }
.md :deep(ul) { margin: 4px 0 4px 18px; }
.md :deep(li) { margin: 2px 0; }
.md :deep(code) {
  background: rgba(0, 0, 0, 0.4);
  padding: 1px 5px;
  border-radius: 4px;
  font-family: Menlo, monospace;
  font-size: 12px;
  color: #9ecbff;
}
.md :deep(pre) {
  background: #14161d;
  border: 1px solid #3c3c3c;
  border-radius: 6px;
  padding: 8px 10px;
  overflow-x: auto;
  margin: 6px 0;
}
.md :deep(pre code) { background: none; padding: 0; }
.md :deep(blockquote) {
  border-left: 3px solid #4a4f63;
  padding-left: 10px;
  margin: 6px 0;
  color: #9aa3bd;
}
.md :deep(table) {
  border-collapse: collapse;
  margin: 6px 0;
  font-size: 12px;
}
.md :deep(th), .md :deep(td) {
  border: 1px solid #4a4f63;
  padding: 4px 8px;
}
.md :deep(th) { background: rgba(255, 255, 255, 0.06); }
.md :deep(a) { color: #22d3ee; }
.md :deep(hr) { border: none; border-top: 1px dashed #4a4f63; margin: 10px 0; }

.msg-think .msg-text {
  background: transparent;
  color: #777;
  font-size: 12px;
  border-left: 2px solid #3c3c3c;
  border-radius: 0;
}

.tool-line {
  margin: 4px 0;
}

.tool-chip {
  display: inline-block;
  padding: 2px 8px;
  background: #2d2d30;
  border-radius: 10px;
  color: #888;
  font-size: 11px;
  font-family: Menlo, monospace;
}

.turn-divider {
  margin: 14px 0;
  border-top: 1px dashed #3c3c3c;
}

.chat-input {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  padding-bottom: 48px;
  padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
  background: #252526;
  border-top: 1px solid #3c3c3c;
}

.chat-input input {
  flex: 1;
  padding: 10px 12px;
  background: #3c3c3c;
  border: 1px solid #555;
  border-radius: 4px;
  color: #d4d4d4;
  font-size: 14px;
  outline: none;
}

.chat-input input:focus {
  border-color: #007acc;
}

.chat-input button {
  padding: 10px 18px;
  background: #007acc;
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  cursor: pointer;
}

.send-error {
  padding: 6px 12px;
  color: #f14c4c;
  font-size: 12px;
  background: #252526;
}

.empty {
  padding: 24px;
  color: #777;
  text-align: center;
  font-size: 13px;
}

@media (max-width: 768px) {
  .header {
    font-size: 12px;
    padding: 8px 12px;
  }

  .fn-bar {
    padding: 6px 8px;
    gap: 5px;
    /* 注意：这个 padding 简写会覆盖基础规则的 padding-bottom，抬升必须在这里重复声明 */
    padding-bottom: var(--nav-lift, 72px);
    padding-bottom: calc(var(--nav-lift, 72px) + env(safe-area-inset-bottom, 0px));
  }

  .fn-bar button {
    min-width: 40px;
    padding: 9px 10px;
    font-size: 13px;
  }
}
</style>
